import { Injectable, defineInjectable, inject, Component, NgModule, ViewEncapsulation, EventEmitter, Output, Directive, TemplateRef, ViewContainerRef, Input, LOCALE_ID, enableProdMode } from '@angular/core';
import { HttpHeaders, HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CommonModule, registerLocaleData } from '@angular/common';
import { Validators, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService, TranslateLoader } from '@ngx-translate/core';
import { Router, ActivatedRoute, RouterModule, NavigationEnd, RouteConfigLoadStart, RouteConfigLoadEnd } from '@angular/router';
import { filter, map, take, catchError, switchMap, finalize } from 'rxjs/operators';
import { JwksValidationHandler, OAuthService, OAuthModule } from 'angular-oauth2-oidc';
import { BehaviorSubject, forkJoin, throwError, from } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { AvatarModule } from 'ngx-avatar';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DropdownModule } from 'primeng/dropdown';
import { MenubarModule } from 'primeng/menubar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ProgressBarModule } from 'primeng/progressbar';
import { SidebarModule } from 'primeng/sidebar';
import { SlideMenuModule } from 'primeng/slidemenu';
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import localePt from '@angular/common/locales/pt';
import localePtExtra from '@angular/common/locales/extra/pt';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { fromFetch } from 'rxjs/fetch';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Referencia para a configuração global
 * @type {?}
 */
const reference = {
    config: {
        contexto: '',
        wso2Url: '',
        debug: false
    }
};
/** @type {?} */
let callbacks = [];
/** @type {?} */
let resolved = false;
/**
 * Serviço estático para gerenciar a configuração da aplicação por ambiente de execução
 */
class MapaEnviromentService {
    /**
     * Define a configuração do ambiente. Deve ser invocado apenas uma única vez no fluxo de vida da aplicação. A chamada desse método é
     * feita pelo componennte MAPA CORE, durante a inicialização do APP
     * @param {?} newConfig
     * @return {?}
     */
    static set(newConfig) {
        if (resolved) {
            throw new Error('MapaEnviromentService estado inválido. Não é permitido a execução deste método mais de uma vez.');
        }
        /** @type {?} */
        let attr;
        for (attr in newConfig) {
            if (!newConfig.hasOwnProperty(attr)) {
                continue;
            }
            reference.config[attr] = newConfig[attr];
        }
        resolved = true;
        callbacks.forEach((/**
         * @param {?} callback
         * @return {?}
         */
        callback => {
            callback(reference.config);
        }));
        // Limpa as referencias
        callbacks = [];
    }
    /**
     * Obtém a configuração atual.
     *
     * ATENÇÃO: O retorno desse método não garante o carregamento da configuração do ambiente. Use o "callback" para aguardar o
     * carregamento do environment.json antes de executar alguma ação de forma segura
     *
     * @param {?=} callback Permite esperar pela definição da configuração do ambiente antes de executar o callback
     * @return {?}
     */
    static get(callback) {
        if (callback) {
            if (resolved) {
                callback(reference.config);
            }
            else {
                callbacks.push(callback);
            }
        }
        return reference.config;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Classe para instanciação de clientes de acesso ao WSO2
 */
class WSO2Client {
    /**
     * @param {?} api
     * @param {?} version
     * @param {?} http
     * @param {?=} accessToken
     */
    constructor(api, version, http, accessToken) {
        this.api = api;
        this.version = version;
        this.http = http;
        this.accessToken = accessToken;
        MapaEnviromentService.get((/**
         * @param {?} env
         * @return {?}
         */
        (env) => {
            this.wso2Url = env.wso2Url;
        }));
    }
    /**
     * @param {?} endpoint
     * @return {?}
     */
    getUrl(endpoint) {
        /** @type {?} */
        let url = `${this.wso2Url}${this.api}/${this.version}${endpoint}`;
        /** @type {?} */
        const config = MapaEnviromentService.get();
        if (config.localUrl && this.api !== 'MapaAuthorization' && config.ambiente.toLowerCase() === 'local') {
            url = `${config.localUrl}${endpoint}`;
        }
        return url;
    }
    /**
     * @return {?}
     */
    getHttpClient() {
        return this.http;
    }
    /**
     * Executa uma chamada GET na API
     * @template T
     * @param {?} endpoint
     * @param {?=} params
     * @return {?}
     */
    get(endpoint, params) {
        /** @type {?} */
        const url = this.getUrl(endpoint);
        /** @type {?} */
        const httpOptions = {
            headers: this.getHeaders(),
            params: params
        };
        return this.http.get(url, httpOptions);
    }
    /**
     * Executa uma chamada POST na API
     * @template T
     * @param {?} endpoint
     * @param {?} body
     * @param {?=} params
     * @return {?}
     */
    post(endpoint, body, params) {
        /** @type {?} */
        const url = this.getUrl(endpoint);
        /** @type {?} */
        const httpOptions = {
            headers: this.getHeaders(),
            params: params
        };
        return this.http.post(url, body, httpOptions);
    }
    /**
     * Executa uma chamada PUT na API
     * @template T
     * @param {?} endpoint
     * @param {?} body
     * @param {?=} params
     * @return {?}
     */
    put(endpoint, body, params) {
        /** @type {?} */
        const url = this.getUrl(endpoint);
        /** @type {?} */
        const httpOptions = {
            headers: this.getHeaders(),
            params: params
        };
        return this.http.put(url, body, httpOptions);
    }
    /**
     * Executa uma chamada DELETE na API
     * @template T
     * @param {?} endpoint
     * @param {?=} params
     * @return {?}
     */
    delete(endpoint, params) {
        /** @type {?} */
        const url = this.getUrl(endpoint);
        /** @type {?} */
        const httpOptions = {
            headers: this.getHeaders(),
            params: params
        };
        return this.http.delete(url, httpOptions);
    }
    /**
     * Obtém o Cabeçalho padrão
     * @private
     * @return {?}
     */
    getHeaders() {
        /** @type {?} */
        const headers = new HttpHeaders({ 'Accept': 'application/json' });
        // Quando a API é publica, não existe o token
        if (this.accessToken) {
            headers.append('Authorization', 'Bearer ' + this.accessToken);
        }
        return headers;
    }
}
/**
 * Componente genérico para criação de clientes WSO2
 */
class WSO2 {
    /**
     * @param {?} http
     */
    constructor(http) {
        this.http = http;
    }
    /**
     * Obtém a instancia de um Client Wso2
     * @param {?} api
     * @param {?} version
     * @param {?=} accessToken
     * @return {?}
     */
    getClient(api, version, accessToken) {
        return new WSO2Client(api, version, this.http, accessToken);
    }
}
WSO2.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];
/** @nocollapse */
WSO2.ctorParameters = () => [
    { type: HttpClient }
];
/** @nocollapse */ WSO2.ngInjectableDef = defineInjectable({ factory: function WSO2_Factory() { return new WSO2(inject(HttpClient)); }, token: WSO2, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Api de integração com o WSO2 para obter os dados da aplicação atual
 */
class AplicacaoAPI {
    /**
     * @param {?} wso2
     */
    constructor(wso2) {
        this.wso2 = wso2;
        this.wso2Client = wso2.getClient('MapaAuthorization', '1.0.0');
    }
    /**
     * Obtém os detalhes da aplicação atual
     * @return {?}
     */
    obterAplicacao() {
        if (!this.promise_obterAplicacao) {
            this.promise_obterAplicacao = new Promise((/**
             * @param {?} accept
             * @param {?} reject
             * @return {?}
             */
            (accept, reject) => {
                if (this.aplicacao) {
                    this.promise_obterAplicacao = null;
                    return accept(this.aplicacao);
                }
                MapaEnviromentService.get((/**
                 * @param {?} config
                 * @return {?}
                 */
                (config) => {
                    if (config.mock && config.mock.aplicacao) {
                        this.aplicacao = this.extrairApplicacao(config.mock.aplicacao);
                        this.promise_obterAplicacao = null;
                        accept(this.aplicacao);
                    }
                    else {
                        return this.wso2Client
                            .get(`/ConsultaAplicacao/${config.contexto}`).toPromise()
                            .then((/**
                         * @param {?} data
                         * @return {?}
                         */
                        (data) => {
                            this.aplicacao = this.extrairApplicacao(data.aplicacao);
                            this.promise_obterAplicacao = null;
                            accept(this.aplicacao);
                        }))
                            .catch((/**
                         * @param {?} reason
                         * @return {?}
                         */
                        reason => {
                            this.promise_obterAplicacao = null;
                            reject(reason);
                        }));
                    }
                }));
            }));
        }
        return this.promise_obterAplicacao;
    }
    /**
     * Obtém a aplicação, apenas se esta for pública
     * @return {?}
     */
    obterAplicacaoSePublica() {
        if (!this.promise_obterAplicacaoSePublica) {
            this.promise_obterAplicacaoSePublica = new Promise((/**
             * @param {?} accept
             * @param {?} reject
             * @return {?}
             */
            (accept, reject) => {
                if (this.aplicacaoPublica === true) {
                    return this.obterAplicacao()
                        .then((/**
                     * @param {?} app
                     * @return {?}
                     */
                    (app) => {
                        this.promise_obterAplicacaoSePublica = null;
                        accept(app);
                    }))
                        .catch((/**
                     * @param {?} reason
                     * @return {?}
                     */
                    reason => {
                        this.promise_obterAplicacaoSePublica = null;
                        reject(reason);
                    }));
                }
                else if (this.aplicacaoPublica === false) {
                    return accept(null);
                }
                MapaEnviromentService.get((/**
                 * @param {?} config
                 * @return {?}
                 */
                (config) => {
                    if (config.mock && config.mock.aplicacao) {
                        this.aplicacaoPublica = true;
                        this.obterAplicacao()
                            .then((/**
                         * @param {?} app
                         * @return {?}
                         */
                        (app) => {
                            this.promise_obterAplicacaoSePublica = null;
                            accept(app);
                        }))
                            .catch((/**
                         * @param {?} reason
                         * @return {?}
                         */
                        reason => {
                            this.promise_obterAplicacaoSePublica = null;
                            reject(reason);
                        }));
                    }
                    else {
                        this.obterAplicacao()
                            .then((/**
                         * @param {?} app
                         * @return {?}
                         */
                        (app) => {
                            this.wso2Client
                                .get('/APPPublicas').toPromise()
                                .then((/**
                             * @param {?} data
                             * @return {?}
                             */
                            (data) => {
                                this.promise_obterAplicacaoSePublica = null;
                                /** @type {?} */
                                const publica = data.apps.app.find((/**
                                 * @param {?} value
                                 * @return {?}
                                 */
                                value => {
                                    return this.extrairApplicacao(value).id === app.id;
                                }));
                                if (publica) {
                                    this.aplicacaoPublica = true;
                                    accept(app);
                                }
                                else {
                                    this.aplicacaoPublica = false;
                                    accept(null);
                                }
                            }))
                                .catch((/**
                             * @param {?} reason
                             * @return {?}
                             */
                            reason => {
                                this.promise_obterAplicacaoSePublica = null;
                                reject(reason);
                            }));
                        }))
                            .catch((/**
                         * @param {?} reason
                         * @return {?}
                         */
                        reason => {
                            this.promise_obterAplicacaoSePublica = null;
                            reject(reason);
                        }));
                    }
                }));
            }));
        }
        return this.promise_obterAplicacaoSePublica;
    }
    /**
     * @private
     * @param {?} response
     * @return {?}
     */
    extrairApplicacao(response) {
        return {
            id: Number.parseInt(response.ID_APLICACAO, 10),
            nome: response.NM_APLICACAO,
            versao: response.DS_VERSAO,
            sigla: response.SG_APLICACAO,
            objetivo: response.DS_OBJETIVO,
            usuarioExterno: response.ST_USUARIO_EXTERNO === 'S',
            usuarioEstrangeiro: response.ST_USUARIO_ESTRANGEIRO === 'S'
        };
    }
}
AplicacaoAPI.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
AplicacaoAPI.ctorParameters = () => [
    { type: WSO2 }
];
/** @nocollapse */ AplicacaoAPI.ngInjectableDef = defineInjectable({ factory: function AplicacaoAPI_Factory() { return new AplicacaoAPI(inject(WSO2)); }, token: AplicacaoAPI, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @enum {string} */
const TIPO_MODULO = {
    DOMINIO: 'DO',
    ITEM_DE_MENU: 'IM',
    SERVICO: 'SV',
    WEB_SERVICE: 'WS',
    WEB_SERVICE_DELETE: 'WS_DELETE',
    WEB_SERVICE_GET: 'WS_GET',
    WEB_SERVICE_POST: 'WS_POST',
    WEB_SERVICE_PUT: 'WS_PUT',
};
/**
 * Api de integração com o WSO2 para obter os dados da aplicação atual
 */
class ModulosAPI {
    /**
     * @param {?} wso2
     */
    constructor(wso2) {
        this.wso2 = wso2;
        this.wso2Client = wso2.getClient('MapaAuthorization', '1.0.0');
    }
    /**
     * Obtém todos os módulos habilitados para o grupo informado, apenas para a aplicação atual
     * @param {?} idGrupo
     * @return {?}
     */
    obterModulosGrupo(idGrupo) {
        return this.obterGrupoXrefModulos()
            .then((/**
         * @param {?} grupoXModulos
         * @return {?}
         */
        grupoXModulos => {
            if (grupoXModulos.hasOwnProperty(idGrupo)) {
                return grupoXModulos[idGrupo];
            }
            return [];
        }));
    }
    /**
     * Obtém todos os módulos Públicos da aplicação atual
     * @return {?}
     */
    obterModulosPublicos() {
        return this.obterModulosAplicacao().then((/**
         * @param {?} modulos
         * @return {?}
         */
        modulos => {
            /** @type {?} */
            const out = modulos.filter((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => modulo.publico));
            // Se possuir algum modulo publico. Torna o modo principal como publico tambéms
            if (out.length > 0) {
                /** @type {?} */
                const mod = modulos.find((/**
                 * @param {?} modulo
                 * @return {?}
                 */
                modulo => {
                    return !modulo.pai || modulo.pai === 0;
                }));
                if (out.indexOf(mod) < 0) {
                    out.push(mod);
                }
            }
            return out;
        }));
    }
    /**
     * @return {?}
     */
    obterModulosAplicacao() {
        if (!this.promise_obterModulosAplicacao) {
            this.promise_obterModulosAplicacao = new Promise((/**
             * @param {?} accept
             * @param {?} reject
             * @return {?}
             */
            (accept, reject) => {
                if (this.modulos) {
                    this.promise_obterModulosAplicacao = null;
                    return accept(this.modulos);
                }
                MapaEnviromentService.get((/**
                 * @param {?} config
                 * @return {?}
                 */
                (config) => {
                    if (config.mock && config.mock.modulos) {
                        this.modulos = config.mock.modulos
                            .map(this.extrairModulo)
                            // Apenas módulos de Frontend (DOMINIO, ITEM_DE_MENU e SERVICO)
                            .filter((/**
                         * @param {?} modulo
                         * @return {?}
                         */
                        modulo => {
                            return [TIPO_MODULO.DOMINIO, TIPO_MODULO.ITEM_DE_MENU, TIPO_MODULO.SERVICO].includes(modulo.tipo);
                        }));
                        this.promise_obterModulosAplicacao = null;
                        accept(this.modulos);
                    }
                    else {
                        /*
                         * Obtém os dados da API do WSO2
                         *
                         * {
                         *    "modulos": {
                         *      "modulo": [
                         *        {
                         *          "ID_MODULO": "43894",
                         *          "ID_MODULO_PAI": "43812",
                         *          "NM_MODULO": "Demandante",
                         *          "NM_ACAO": "#",
                         *          "CS_MODULO": "WS",
                         *          "ST_PUBLICO": "N",
                         *          "ST_SAIDA_WS": "N",
                         *          "NR_ORDEM_MODULO": "2",
                         *        },
                         *      ]
                         *    }
                         *  }
                         */
                        return this.wso2Client
                            .get(`/ConsultaModulosAplicacao/${config.contexto}`).toPromise()
                            .then((/**
                         * @param {?} data
                         * @return {?}
                         */
                        (data) => {
                            if (data.modulos && data.modulos.modulo) {
                                this.modulos = data.modulos.modulo
                                    .map(this.extrairModulo)
                                    // Apenas módulos de Frontend (DOMINIO, ITEM_DE_MENU e SERVICO)
                                    .filter((/**
                                 * @param {?} modulo
                                 * @return {?}
                                 */
                                modulo => {
                                    return [TIPO_MODULO.DOMINIO, TIPO_MODULO.ITEM_DE_MENU, TIPO_MODULO.SERVICO].includes(modulo.tipo);
                                }));
                            }
                            else {
                                this.modulos = [];
                            }
                            this.promise_obterModulosAplicacao = null;
                            accept(this.modulos);
                        }))
                            .catch((/**
                         * @param {?} reason
                         * @return {?}
                         */
                        reason => {
                            this.promise_obterModulosAplicacao = null;
                            reject(reason);
                        }));
                    }
                }));
            }));
        }
        return this.promise_obterModulosAplicacao;
    }
    /**
     * Obtém o mapeamento cruzado entren ID_GRUPO -> [MODULO]
     * @return {?}
     */
    obterGrupoXrefModulos() {
        if (!this.promise_obterGrupoXrefModulos) {
            this.promise_obterGrupoXrefModulos = new Promise((/**
             * @param {?} accept
             * @param {?} reject
             * @return {?}
             */
            (accept, reject) => {
                if (this.gruposModulos) {
                    this.promise_obterGrupoXrefModulos = null;
                    return accept(this.gruposModulos);
                }
                MapaEnviromentService.get((/**
                 * @param {?} config
                 * @return {?}
                 */
                (config) => {
                    this.obterModulosAplicacao()
                        .then((/**
                     * @return {?}
                     */
                    () => {
                        if (config.mock && config.mock.modulos_grupos) {
                            return this.extrairModulosGrupos(config.mock.modulos_grupos)
                                .then((/**
                             * @param {?} gruposModulos
                             * @return {?}
                             */
                            gruposModulos => {
                                this.gruposModulos = gruposModulos;
                                this.promise_obterGrupoXrefModulos = null;
                                accept(this.gruposModulos);
                            }));
                        }
                        else {
                            /*
                             * {
                             *    "modulos_grupos": {
                             *      "modulo_grupo": [
                             *        {
                             *          "NM_MODULO": "HUB",
                             *          "ID_MODULO": "43812",
                             *          "ID_GRUPO": "15813"
                             *        },
                             *      ]
                             *    }
                             *  }
                             */
                            return this.wso2Client
                                .get(`/ConsultaGruposModulos/${config.contexto}`).toPromise()
                                .then((/**
                             * @param {?} data
                             * @return {?}
                             */
                            (data) => {
                                if (data.modulos_grupos && data.modulos_grupos.modulo_grupo) {
                                    return this.extrairModulosGrupos(data.modulos_grupos.modulo_grupo)
                                        .then((/**
                                     * @param {?} gruposModulos
                                     * @return {?}
                                     */
                                    gruposModulos => {
                                        this.gruposModulos = gruposModulos;
                                        this.promise_obterGrupoXrefModulos = null;
                                        accept(this.gruposModulos);
                                    }));
                                }
                                else {
                                    this.promise_obterGrupoXrefModulos = null;
                                    this.gruposModulos = {};
                                    accept(this.gruposModulos);
                                }
                            }));
                        }
                    }))
                        .catch((/**
                     * @param {?} reason
                     * @return {?}
                     */
                    reason => {
                        this.promise_obterGrupoXrefModulos = null;
                        reject(reason);
                    }));
                }));
            }));
        }
        return this.promise_obterGrupoXrefModulos;
    }
    /**
     * @private
     * @param {?} registros
     * @return {?}
     */
    extrairModulosGrupos(registros) {
        return this.obterModulosAplicacao()
            .then((/**
         * @param {?} modulosAplicacao
         * @return {?}
         */
        modulosAplicacao => {
            /** @type {?} */
            const out = {};
            registros.forEach((/**
             * @param {?} registro
             * @return {?}
             */
            registro => {
                /** @type {?} */
                const idGrupo = Number.parseInt(registro.ID_GRUPO, 10);
                /** @type {?} */
                const idModulo = Number.parseInt(registro.ID_MODULO, 10);
                if (!out[idGrupo]) {
                    out[idGrupo] = [];
                }
                /** @type {?} */
                const modulo = modulosAplicacao.find((/**
                 * @param {?} it
                 * @return {?}
                 */
                it => it.id === idModulo));
                if (modulo && !out[idGrupo].find((/**
                 * @param {?} it
                 * @return {?}
                 */
                it => it.id === idModulo))) {
                    out[idGrupo].push(modulo);
                }
            }));
            return out;
        }));
    }
    /**
     * @private
     * @param {?} dados
     * @return {?}
     */
    extrairModulo(dados) {
        /** @type {?} */
        const role = {
            id: Number.parseInt(dados.ID_MODULO, 10),
            pai: dados.ID_MODULO_PAI ? Number.parseInt(dados.ID_MODULO_PAI, 10) : undefined,
            ordem: Number.parseInt(dados.NR_ORDEM_MODULO, 10),
            nome: dados.NM_MODULO,
            acao: dados.NM_ACAO,
            publico: dados.ST_PUBLICO === 'S',
            tipo: dados.CS_MODULO
        };
        return role;
    }
}
ModulosAPI.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
ModulosAPI.ctorParameters = () => [
    { type: WSO2 }
];
/** @nocollapse */ ModulosAPI.ngInjectableDef = defineInjectable({ factory: function ModulosAPI_Factory() { return new ModulosAPI(inject(WSO2)); }, token: ModulosAPI, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class Usuario {
    /**
     * @param {?} perfil
     * @return {?}
     */
    hasPerfil(perfil) {
        return this.perfis.findIndex((/**
         * @param {?} value
         * @return {?}
         */
        value => value === perfil)) !== -1;
    }
    /**
     * @param {?} perfis
     * @return {?}
     */
    hasAnyPerfil(perfis) {
        for (let i = 0; i < perfis.length; i++) {
            if (this.hasPerfil(perfis[i])) {
                return true;
            }
        }
        return false;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Api de integração com o WSO2 para obter os grupos da aplicação atual
 */
class GruposAPI {
    /**
     * @param {?} wso2
     */
    constructor(wso2) {
        this.wso2 = wso2;
        // this.grupos = [];
        this.wso2Client = wso2.getClient('MapaAuthorization', '1.0.0');
    }
    /**
     * Obtém todos os grupos da aplicação atual
     * @return {?}
     */
    obterGruposAplicacao() {
        if (!this.promise_obterGruposAplicacao) {
            this.promise_obterGruposAplicacao = new Promise((/**
             * @param {?} accept
             * @param {?} reject
             * @return {?}
             */
            (accept, reject) => {
                if (this.grupos) {
                    this.promise_obterGruposAplicacao = null;
                    return accept(this.grupos);
                }
                MapaEnviromentService.get((/**
                 * @param {?} config
                 * @return {?}
                 */
                (config) => {
                    if (config.mock && config.mock.grupos) {
                        this.grupos = config.mock.grupos.map(this.extrairGrupo);
                        this.promise_obterGruposAplicacao = null;
                        accept(this.grupos);
                    }
                    else {
                        /*
                         * Obtém os dados da API do WSO2
                         *
                         * {
                         *    "grupos": {
                         *      "grupo": [
                         *        {
                         *          "ID_GRUPO": "15813",
                         *          "ID_GRUPO_PAI": null,
                         *          "NM_GRUPO": "Administrador"
                         *          "ST_ACESSO_ESTRANGEIRO": "N",
                         *          "ST_ACESSO_EXTERNO": "N",
                         *        },
                         *        {
                         *          "ID_GRUPO": "15814",
                         *          "ID_GRUPO_PAI": null,
                         *          "ST_ACESSO_ESTRANGEIRO": "N",
                         *          "ST_ACESSO_EXTERNO": "N",
                         *          "NM_GRUPO": "Gestor"
                         *        }
                         *      ]
                         *    }
                         *  }
                         */
                        return this.wso2Client
                            .get(`/ConsultaGruposAplicacao/${config.contexto}`).toPromise()
                            .then((/**
                         * @param {?} data
                         * @return {?}
                         */
                        (data) => {
                            if (data.grupos && data.grupos.grupo) {
                                this.grupos = data.grupos.grupo.map(this.extrairGrupo);
                            }
                            else {
                                this.grupos = [];
                            }
                            this.promise_obterGruposAplicacao = null;
                            accept(this.grupos);
                        }))
                            .catch((/**
                         * @param {?} reason
                         * @return {?}
                         */
                        reason => {
                            this.promise_obterGruposAplicacao = null;
                            reject(reason);
                        }));
                    }
                }));
            }));
        }
        return this.promise_obterGruposAplicacao;
    }
    /**
     * Obtém os detalhes de um Grupo por id.
     * <p>
     * Só faz a consulta nos Grupos da aplicação atual
     * @param {?} idGrupo
     * @return {?}
     */
    obterGrupoPorId(idGrupo) {
        return this.obterGruposAplicacao()
            .then((/**
         * @param {?} grupos
         * @return {?}
         */
        (grupos) => {
            return grupos.find((/**
             * @param {?} grupo
             * @return {?}
             */
            grupo => grupo.id === idGrupo));
        }));
    }
    /**
     * @private
     * @param {?} dados
     * @return {?}
     */
    extrairGrupo(dados) {
        return {
            id: Number.parseInt(dados.ID_GRUPO, 10),
            pai: dados.ID_GRUPO_PAI
                ? Number.parseInt(dados.ID_GRUPO_PAI, 10)
                : undefined,
            nome: dados.NM_GRUPO,
            acessoEstrangeiro: dados.ST_ACESSO_ESTRANGEIRO === 'S',
            acessoExterno: dados.ST_ACESSO_EXTERNO === 'S'
        };
    }
}
GruposAPI.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
GruposAPI.ctorParameters = () => [
    { type: WSO2 }
];
/** @nocollapse */ GruposAPI.ngInjectableDef = defineInjectable({ factory: function GruposAPI_Factory() { return new GruposAPI(inject(WSO2)); }, token: GruposAPI, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class Wso2IntegrationService {
    /**
     * @param {?} oauthService
     * @param {?} wso2
     * @param {?} aplicacaoAPI
     * @param {?} gruposAPI
     * @param {?} modulosAPI
     * @param {?} router
     */
    constructor(oauthService, wso2, aplicacaoAPI, gruposAPI, modulosAPI, router) {
        this.oauthService = oauthService;
        this.wso2 = wso2;
        this.aplicacaoAPI = aplicacaoAPI;
        this.gruposAPI = gruposAPI;
        this.modulosAPI = modulosAPI;
        this.router = router;
        this.STORAGE = localStorage;
        this.runningLoadSuject = false;
        this.MAPA_MOCK_STORAGE_KEY = 'MAPA_MOCK_ACCESS_TOKEN';
        this.aplicacao = new BehaviorSubject(null);
        this.modulos = new BehaviorSubject([]);
        this.usuario = new BehaviorSubject(null);
        /** @type {?} */
        let wso2ApiName = MapaEnviromentService.get().wso2ApiName;
        if (!wso2ApiName) {
            wso2ApiName = MapaEnviromentService.get().contexto.replace('_api', '');
        }
        this.wso2Client = this.wso2.getClient(wso2ApiName, '1.0.0', null);
        this.configureOauthImplicitFlowAuthentication();
        setTimeout((/**
         * @return {?}
         */
        () => this.loadSubject()), 250);
        // Configuração para evitar erro de rotas quando a url for "#access_token=ehd..."
        this.router.events.pipe(filter((/**
         * @param {?} event
         * @return {?}
         */
        event => {
            return (event.toString()).startsWith('NavigationStart');
        })), filter((/**
         * @param {?} event
         * @return {?}
         */
        event => {
            return (/access_token|id_token|error/).test(((/** @type {?} */ (event))).url);
        }))).subscribe((/**
         * @param {?} event
         * @return {?}
         */
        event => {
            console.log('Wso2IntegrationService :: Iniciando processo de autenticação...', event);
            this.oauthService.tryLogin()
                .then((/**
             * @param {?} value
             * @return {?}
             */
            value => {
                console.log('Wso2IntegrationService :: tryLogin() = ' + value);
                this.loadSubject().catch((/**
                 * @param {?} reason
                 * @return {?}
                 */
                reason => {
                    console.error('Wso2IntegrationService :: tryLogin() -> loadSubject()', reason);
                }));
            }))
                .catch((/**
             * @param {?} reason
             * @return {?}
             */
            reason => console.error('Wso2IntegrationService :: tryLogin()', reason)));
            this.router.navigateByUrl('/')
                .then((/**
             * @param {?} value
             * @return {?}
             */
            value => console.log(value)));
        }));
    }
    /**
     * @private
     * @return {?}
     */
    static userIsMocked() {
        return !!MapaEnviromentService.get().mock && !!MapaEnviromentService.get().mock.jwt;
    }
    /**
     * O WSO2 entrega a string "null" quando o campo é vazio
     * @private
     * @param {?} value
     * @return {?}
     */
    static ignoreNullString(value) {
        if (value === '' || value === 'null') {
            return null;
        }
        return value;
    }
    /**
     * @return {?}
     */
    getJWT() {
        /** @type {?} */
        const token = this.getTokenJWT();
        return !!token ? this.decodeToken(token) : null;
    }
    /**
     * @param {?} token
     * @return {?}
     */
    decodeToken(token) {
        return JSON.parse(atob(token.split('.')[1]));
    }
    /**
     * Obtém o token JWT proveniente do Oauth2 ou o valor MOCK (se existir)
     * @return {?}
     */
    getTokenJWT() {
        if (Wso2IntegrationService.userIsMocked()) {
            return this.STORAGE.getItem(this.MAPA_MOCK_STORAGE_KEY);
        }
        return this.oauthService.getAccessToken();
    }
    /**
     * @private
     * @return {?}
     */
    sanitizeTokenJWT() {
        /** @type {?} */
        const jwt = this.getJWT();
        if (!jwt) {
            return null;
        }
        /** @type {?} */
        const normalizedName = jwt.nome
            .toUpperCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
        /** @type {?} */
        const sanitizedJWT = {
            Nome: jwt.Nome,
            idPessoa: jwt.idPessoa,
            nome: normalizedName,
            email: jwt.email,
            documento: jwt.documento,
            idUsuario: jwt.idUsuario,
            Aplicacoes: jwt.Aplicacoes,
            Grupos: jwt.Grupos,
            ativo: jwt.ativo,
            VALIDO: jwt.VALIDO,
            ACCESS_TOKEN: jwt.ACCESS_TOKEN,
            REFRESH_TOKEN: jwt.REFRESH_TOKEN,
            BASIC: jwt.BASIC,
            CONSUMER_KEY: jwt.CONSUMER_KEY,
            CONSUMER_SECRET: jwt.CONSUMER_SECRET,
        };
        /** @type {?} */
        const sanitizedToken = this.prepareJWT(sanitizedJWT);
        if (Wso2IntegrationService.userIsMocked()) {
            this.STORAGE.setItem(this.MAPA_MOCK_STORAGE_KEY, sanitizedToken);
        }
        else {
            this.STORAGE.setItem('access_token', sanitizedToken);
        }
        return sanitizedJWT;
    }
    /**
     * @private
     * @param {?} body
     * @return {?}
     */
    prepareJWT(body) {
        /** @type {?} */
        const header = this.getTokenJWT().split('.')[0];
        /** @type {?} */
        const tokenString = header + '.' + btoa(JSON.stringify(body)) + '.';
        return tokenString.replace(/=/g, '');
    }
    /**
     * Efetua o login do usuário, usando o mock.jwt ou a autenticação no SSO (WSO2)
     * @return {?}
     */
    login() {
        if (Wso2IntegrationService.userIsMocked()) {
            /** @type {?} */
            const token = MapaEnviromentService.get().mock.jwt;
            this.STORAGE.setItem(this.MAPA_MOCK_STORAGE_KEY, token);
            this.loadSubject().catch((/**
             * @param {?} reason
             * @return {?}
             */
            reason => {
                console.error('Wso2IntegrationService :: login() -> loadSubject()', reason);
            }));
            return;
        }
        this.oauthService.initImplicitFlow();
    }
    /**
     * Finaliza a sessão do usuário
     * @return {?}
     */
    logout() {
        /** @type {?} */
        const config = MapaEnviromentService.get();
        /** @type {?} */
        const idToken = this.oauthService.getIdToken();
        /** @type {?} */
        const redirectUri = config.sso.redirectUri;
        /** @type {?} */
        const logoutUri = config.sso.serverUrl.concat('/oidc/logout');
        this.STORAGE.clear();
        if (Wso2IntegrationService.userIsMocked()) {
            this.usuario.next(null);
            this.modulos.next([]);
            this.router.navigateByUrl('/').catch((/**
             * @param {?} reason
             * @return {?}
             */
            reason => console.error(reason)));
            return;
        }
        // Limpa dados
        this.oauthService.logOut();
        // Fazer logout no SSO
        // https://docs.wso2.com/display/IS540/OpenID+Connect+Logout+URL+Redirection
        window.location.href = `${logoutUri}?id_token_hint=${idToken}&post_logout_redirect_uri=${redirectUri}&state=1`;
    }
    /**
     * Verifica se o usuário está logado
     * @return {?}
     */
    isLoggedIn() {
        return new Promise((/**
         * @param {?} resolve
         * @param {?} reject
         * @return {?}
         */
        (resolve, reject) => {
            /** @type {?} */
            const jwt = this.getTokenJWT();
            if (!!jwt) {
                resolve(true);
            }
            else {
                resolve(false);
            }
        }));
    }
    /**
     * @param {?} accessToken
     * @return {?}
     */
    changeAccessToken(accessToken) {
        /** @type {?} */
        const jwt = this.getJWT();
        if (!!jwt) {
            jwt.ACCESS_TOKEN = accessToken;
            jwt.VALIDO = new Date((+new Date()) + 3500000).toISOString();
            /** @type {?} */
            const newTokenJWT = this.prepareJWT(jwt);
            if (Wso2IntegrationService.userIsMocked()) {
                this.STORAGE.setItem(this.MAPA_MOCK_STORAGE_KEY, newTokenJWT);
            }
            else {
                this.STORAGE.setItem('access_token', newTokenJWT);
            }
        }
    }
    /**
     * Obtém a representação do usuário solicitante da requisição.
     *
     * Válido apenas para o "MAPA-JWT-Assertion", proveniente do WSO2
     * @private
     * @return {?}
     */
    loadSubject() {
        if (this.runningLoadSuject) {
            return Promise.resolve();
        }
        this.runningLoadSuject = true;
        console.log('Wso2IntegrationService :: loadSubject');
        /** @type {?} */
        const jwt = this.sanitizeTokenJWT();
        /** @type {?} */
        const modulosUsuario = new Set();
        /** @type {?} */
        let usuarioLogado;
        // Aplicação
        return this.aplicacaoAPI.obterAplicacao()
            .then((/**
         * @param {?} aplicacaoAtual
         * @return {?}
         */
        aplicacaoAtual => {
            console.log('Wso2IntegrationService :: loadSubject - Aplicação: ', aplicacaoAtual);
            this.aplicacao.next(aplicacaoAtual);
        }))
            // Módulos públicos
            .then((/**
         * @return {?}
         */
        () => {
            return this.modulosAPI.obterModulosPublicos().then((/**
             * @param {?} modulosPublicos
             * @return {?}
             */
            modulosPublicos => {
                modulosPublicos.forEach((/**
                 * @param {?} modulo
                 * @return {?}
                 */
                (modulo) => {
                    modulosUsuario.add(modulo);
                }));
            }));
        }))
            // Módulos do usuário logado
            .then((/**
         * @return {?}
         */
        () => {
            // Grupos do Usuário (separados por ";" Ex. "Grupos": "15812;20")
            /** @type {?} */
            let idsGruposUsuario = ((jwt && jwt.Grupos) || '').split(';').filter((/**
             * @param {?} value
             * @return {?}
             */
            value => !!value));
            idsGruposUsuario = [...new Set(idsGruposUsuario)];
            console.log('Wso2IntegrationService :: loadSubject - Grupos: ', idsGruposUsuario);
            return Promise.all(idsGruposUsuario.map((/**
             * @param {?} idGrupo
             * @return {?}
             */
            idGrupo => {
                return this.modulosAPI.obterModulosGrupo(+idGrupo);
            }))).then((/**
             * @param {?} arrayTodosModulos
             * @return {?}
             */
            (arrayTodosModulos) => {
                arrayTodosModulos.forEach((/**
                 * @param {?} modulosPorGrupo
                 * @return {?}
                 */
                (modulosPorGrupo) => {
                    modulosPorGrupo.forEach((/**
                     * @param {?} modulo
                     * @return {?}
                     */
                    (modulo) => {
                        modulosUsuario.add(modulo);
                    }));
                }));
            })).catch(console.error);
        }))
            // Printa os módulos (Públicos & Privados)
            .then((/**
         * @return {?}
         */
        () => {
            console.log('Wso2IntegrationService :: loadSubject - Módulos: ', modulosUsuario);
            this.modulos.next([...modulosUsuario]);
            return Promise.resolve();
        }))
            // Usuário
            .then((/**
         * @return {?}
         */
        () => {
            usuarioLogado = new Usuario();
            if (!!jwt) {
                usuarioLogado.username = jwt.Nome;
                usuarioLogado.idPessoa = Number.parseInt(jwt.idPessoa, 10);
                usuarioLogado.nome = Wso2IntegrationService.ignoreNullString(jwt.nome);
                usuarioLogado.email = Wso2IntegrationService.ignoreNullString(jwt.email);
                usuarioLogado.documento = Wso2IntegrationService.ignoreNullString(jwt.documento);
                usuarioLogado.idUsuario = Number.parseInt(jwt.idUsuario, 10);
                usuarioLogado.perfis = [];
                // S - Usuário de Banco Ativo, A - Usuário de AD, E - Usuário Externo de AD (login termina com .ex)
                usuarioLogado.ativo = ['S', 'A', 'E'].includes(Wso2IntegrationService.ignoreNullString(jwt.ativo));
                return this.wso2Client.get('/api/v1/seguranca/grupos').toPromise()
                    .then((/**
                 * @param {?} resposta
                 * @return {?}
                 */
                resposta => {
                    if (!!resposta.conteudo && Array.isArray(resposta.conteudo)) {
                        usuarioLogado.perfis = resposta.conteudo;
                    }
                    else {
                        console.log('Wso2IntegrationService :: loadSubject - Perfis usuários: ', resposta);
                    }
                })).catch((/**
                 * @param {?} reason
                 * @return {?}
                 */
                reason => {
                    console.error('Wso2IntegrationService :: loadSubject - Perfis usuários: ', reason);
                })).finally((/**
                 * @return {?}
                 */
                () => {
                    console.log('Wso2IntegrationService :: loadSubject - Usuário: ', usuarioLogado);
                    this.usuario.next(usuarioLogado);
                    this.runningLoadSuject = false;
                    return Promise.resolve();
                }));
            }
            else {
                usuarioLogado.username = 'usuariointernet';
                usuarioLogado.nome = 'Anônimo';
                usuarioLogado.perfis = [];
                usuarioLogado.ativo = true;
                console.log('Wso2IntegrationService :: loadSubject - Usuário: ', usuarioLogado);
                this.usuario.next(usuarioLogado);
                this.runningLoadSuject = false;
                return Promise.resolve();
            }
        }));
    }
    /**
     * @private
     * @return {?}
     */
    configureOauthImplicitFlowAuthentication() {
        /** @type {?} */
        const sso = MapaEnviromentService.get().sso;
        /** @type {?} */
        const authConfig = {
            issuer: sso.issuer,
            clientId: sso.clientId,
            tokenEndpoint: sso.serverUrl.concat(sso.tokenEndpoint),
            jwksEndpoint: sso.serverUrl.concat(sso.jwksEndpoint),
            jwks: sso.jwks,
            userinfoEndpoint: sso.serverUrl.concat(sso.userinfoEndpoint),
            showDebugInformation: sso.showDebugInformation,
            loginUrl: sso.serverUrl.concat(sso.authorizationEndpoint),
            requireHttps: sso.requireHttps,
            disableAtHashCheck: true,
            redirectUri: sso.redirectUri,
            // Parametros genéricos
            scope: 'openid profile',
            responseType: 'id_token token'
        };
        this.oauthService.setStorage(this.STORAGE);
        this.oauthService.configure(authConfig);
        // Somente habilita a validação se o jwks estiver definido nas configs
        if (authConfig.jwks) {
            this.oauthService.tokenValidationHandler = new JwksValidationHandler();
        }
    }
}
Wso2IntegrationService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];
/** @nocollapse */
Wso2IntegrationService.ctorParameters = () => [
    { type: OAuthService },
    { type: WSO2 },
    { type: AplicacaoAPI },
    { type: GruposAPI },
    { type: ModulosAPI },
    { type: Router }
];
/** @nocollapse */ Wso2IntegrationService.ngInjectableDef = defineInjectable({ factory: function Wso2IntegrationService_Factory() { return new Wso2IntegrationService(inject(OAuthService), inject(WSO2), inject(AplicacaoAPI), inject(GruposAPI), inject(ModulosAPI), inject(Router)); }, token: Wso2IntegrationService, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class AuthComponent {
    /**
     * @param {?} formBuilder
     * @param {?} route
     * @param {?} router
     * @param {?} wso2IntegrationService
     */
    constructor(formBuilder, route, router, wso2IntegrationService) {
        this.formBuilder = formBuilder;
        this.route = route;
        this.router = router;
        this.wso2IntegrationService = wso2IntegrationService;
        this.loading = false;
        this.submitted = false;
        this.error = '';
    }
    // convenience getter for easy access to form fields
    /**
     * @return {?}
     */
    get f() {
        return this.loginForm.controls;
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required]
        });
        // reset login status
        this.wso2IntegrationService.logout();
        // get return url from route parameters or default to '/'
        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    }
    /**
     * @return {?}
     */
    onSubmit() {
        this.submitted = true;
        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }
        this.loading = true;
        this.wso2IntegrationService.login();
    }
}
AuthComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-auth',
                template: "<div class=\"p-grid p-justify-center\">\r\n    <div class=\"p-col p-col-align-center\">\r\n        <p-toast [style]=\"{marginTop: '80px'}\"></p-toast>\r\n    </div>\r\n</div>\r\n<div class=\"p-grid p-justify-center\">\r\n    <form [formGroup]=\"loginForm\" (ngSubmit)=\"onSubmit()\" class=\"p-col\">\r\n        <p-panel header=\"Autentica\u00E7\u00E3o Corporativa\">\r\n            <div class=\"ui-grid ui-grid-responsive ui-grid-pad ui-fluid\" style=\"margin: 10px 0px\">\r\n                <div class=\"ui-grid-row\">\r\n                    <div class=\"ui-grid-col-2\">\r\n                        {{ 'CORE.AUTH.LOGIN' | translate }}\r\n                    </div>\r\n                    <div class=\"ui-grid-col-6\">\r\n                        <input pInputText type=\"text\" formControlName=\"username\"\r\n                               [placeholder]=\"'CORE.AUTH.LOGIN_PLACEHOLDER' | translate\"/>\r\n                    </div>\r\n                    <div class=\"ui-grid-col-4\">\r\n                        <p-message severity=\"error\" [text]=\"'CORE.AUTH.REQUIRED' | translate\"\r\n                                   *ngIf=\"!loginForm.controls['username'].valid&&loginForm.controls['username'].dirty\"></p-message>\r\n                    </div>\r\n                </div>\r\n                <div class=\"ui-grid-row\">\r\n                    <div class=\"ui-grid-col-2\">\r\n                        {{ 'CORE.AUTH.PASSWORD' | translate }}\r\n                    </div>\r\n                    <div class=\"ui-grid-col-6\">\r\n                        <input pInputText type=\"password\" formControlName=\"password\"\r\n                               [placeholder]=\"'CORE.AUTH.PASSWORD_PLACEHOLDER' | translate\"/>\r\n                    </div>\r\n                    <div class=\"ui-grid-col-4\">\r\n                        <p-message severity=\"error\" [text]=\"'CORE.AUTH.REQUIRED' | translate\"\r\n                                   *ngIf=\"!loginForm.controls['password'].valid&&loginForm.controls['password'].dirty\"></p-message>\r\n                    </div>\r\n                </div>\r\n                <div class=\"ui-grid-row\">\r\n                    <div class=\"ui-grid-col-2\"></div>\r\n                    <div class=\"ui-grid-col-6\">\r\n                        <button pButton type=\"submit\" [label]=\"'CORE.AUTH.CONFIRM' | translate\"\r\n                                [disabled]=\"!loginForm.valid\"></button>\r\n                    </div>\r\n                    <div class=\"ui-grid-col-4\"></div>\r\n                </div>\r\n            </div>\r\n        </p-panel>\r\n    </form>\r\n</div>\r\n",
                styles: ["form{max-width:480px}"]
            }] }
];
/** @nocollapse */
AuthComponent.ctorParameters = () => [
    { type: FormBuilder },
    { type: ActivatedRoute },
    { type: Router },
    { type: Wso2IntegrationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class PublicLayoutComponent {
    constructor() {
        this.show$ = new BehaviorSubject(false);
        // Só exibe após a configuração ser definida pela aplicação
        MapaEnviromentService.get((/**
         * @return {?}
         */
        () => {
            this.show$.next(true);
        }));
    }
}
PublicLayoutComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-public-layout',
                template: "<div *ngIf=\"(show$ | async)\">\r\n    <mapa-header></mapa-header>\r\n    <div class=\"mapa-content-container\">\r\n        <router-outlet></router-outlet>\r\n    </div>\r\n</div>\r\n",
                styles: [""]
            }] }
];
/** @nocollapse */
PublicLayoutComponent.ctorParameters = () => [];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const authRoutes = [
    {
        path: 'auth',
        component: PublicLayoutComponent,
        data: {
            breadcrumbs: 'Login'
        },
        children: [
            { path: '', redirectTo: 'login', pathMatch: 'full' },
            {
                path: 'login',
                component: AuthComponent
            }
        ]
    }
];
/** @type {?} */
const AuthRouting = RouterModule.forChild(authRoutes);

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class AuthModule {
}
AuthModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    ToastModule,
                    PanelModule,
                    ButtonModule,
                    InputTextModule,
                    MessageModule,
                    TranslateModule,
                    AuthRouting
                ],
                declarations: [AuthComponent],
                exports: [AuthComponent]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/* tslint:disable:max-line-length */
/** @type {?} */
const replacementList = [
    {
        base: ' ',
        chars: '\u00A0',
    }, {
        base: '0',
        chars: '\u07C0',
    }, {
        base: 'A',
        chars: '\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F',
    }, {
        base: 'AA',
        chars: '\uA732',
    }, {
        base: 'AE',
        chars: '\u00C6\u01FC\u01E2',
    }, {
        base: 'AO',
        chars: '\uA734',
    }, {
        base: 'AU',
        chars: '\uA736',
    }, {
        base: 'AV',
        chars: '\uA738\uA73A',
    }, {
        base: 'AY',
        chars: '\uA73C',
    }, {
        base: 'B',
        chars: '\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0181',
    }, {
        base: 'C',
        chars: '\u24b8\uff23\uA73E\u1E08\u0106\u0043\u0108\u010A\u010C\u00C7\u0187\u023B',
    }, {
        base: 'D',
        chars: '\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018A\u0189\u1D05\uA779',
    }, {
        base: 'Dh',
        chars: '\u00D0',
    }, {
        base: 'DZ',
        chars: '\u01F1\u01C4',
    }, {
        base: 'Dz',
        chars: '\u01F2\u01C5',
    }, {
        base: 'E',
        chars: '\u025B\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E\u1D07',
    }, {
        base: 'F',
        chars: '\uA77C\u24BB\uFF26\u1E1E\u0191\uA77B',
    }, {
        base: 'G',
        chars: '\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E\u0262',
    }, {
        base: 'H',
        chars: '\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D',
    }, {
        base: 'I',
        chars: '\u24BE\uFF29\xCC\xCD\xCE\u0128\u012A\u012C\u0130\xCF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197',
    }, {
        base: 'J',
        chars: '\u24BF\uFF2A\u0134\u0248\u0237',
    }, {
        base: 'K',
        chars: '\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2',
    }, {
        base: 'L',
        chars: '\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780',
    }, {
        base: 'LJ',
        chars: '\u01C7',
    }, {
        base: 'Lj',
        chars: '\u01C8',
    }, {
        base: 'M',
        chars: '\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C\u03FB',
    }, {
        base: 'N',
        chars: '\uA7A4\u0220\u24C3\uFF2E\u01F8\u0143\xD1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u019D\uA790\u1D0E',
    }, {
        base: 'NJ',
        chars: '\u01CA',
    }, {
        base: 'Nj',
        chars: '\u01CB',
    }, {
        base: 'O',
        chars: '\u24C4\uFF2F\xD2\xD3\xD4\u1ED2\u1ED0\u1ED6\u1ED4\xD5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\xD6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\xD8\u01FE\u0186\u019F\uA74A\uA74C',
    }, {
        base: 'OE',
        chars: '\u0152',
    }, {
        base: 'OI',
        chars: '\u01A2',
    }, {
        base: 'OO',
        chars: '\uA74E',
    }, {
        base: 'OU',
        chars: '\u0222',
    }, {
        base: 'P',
        chars: '\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754',
    }, {
        base: 'Q',
        chars: '\u24C6\uFF31\uA756\uA758\u024A',
    }, {
        base: 'R',
        chars: '\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782',
    }, {
        base: 'S',
        chars: '\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784',
    }, {
        base: 'T',
        chars: '\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786',
    }, {
        base: 'Th',
        chars: '\u00DE',
    }, {
        base: 'TZ',
        chars: '\uA728',
    }, {
        base: 'U',
        chars: '\u24CA\uFF35\xD9\xDA\xDB\u0168\u1E78\u016A\u1E7A\u016C\xDC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244',
    }, {
        base: 'V',
        chars: '\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245',
    }, {
        base: 'VY',
        chars: '\uA760',
    }, {
        base: 'W',
        chars: '\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72',
    }, {
        base: 'X',
        chars: '\u24CD\uFF38\u1E8A\u1E8C',
    }, {
        base: 'Y',
        chars: '\u24CE\uFF39\u1EF2\xDD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE',
    }, {
        base: 'Z',
        chars: '\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762',
    }, {
        base: 'a',
        chars: '\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250\u0251',
    }, {
        base: 'aa',
        chars: '\uA733',
    }, {
        base: 'ae',
        chars: '\u00E6\u01FD\u01E3',
    }, {
        base: 'ao',
        chars: '\uA735',
    }, {
        base: 'au',
        chars: '\uA737',
    }, {
        base: 'av',
        chars: '\uA739\uA73B',
    }, {
        base: 'ay',
        chars: '\uA73D',
    }, {
        base: 'b',
        chars: '\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253\u0182',
    }, {
        base: 'c',
        chars: '\uFF43\u24D2\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184',
    }, {
        base: 'd',
        chars: '\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\u018B\u13E7\u0501\uA7AA',
    }, {
        base: 'dh',
        chars: '\u00F0',
    }, {
        base: 'dz',
        chars: '\u01F3\u01C6',
    }, {
        base: 'e',
        chars: '\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u01DD',
    }, {
        base: 'f',
        chars: '\u24D5\uFF46\u1E1F\u0192',
    }, {
        base: 'ff',
        chars: '\uFB00',
    }, {
        base: 'fi',
        chars: '\uFB01',
    }, {
        base: 'fl',
        chars: '\uFB02',
    }, {
        base: 'ffi',
        chars: '\uFB03',
    }, {
        base: 'ffl',
        chars: '\uFB04',
    }, {
        base: 'g',
        chars: '\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\uA77F\u1D79',
    }, {
        base: 'h',
        chars: '\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265',
    }, {
        base: 'hv',
        chars: '\u0195',
    }, {
        base: 'i',
        chars: '\u24D8\uFF49\xEC\xED\xEE\u0129\u012B\u012D\xEF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131',
    }, {
        base: 'j',
        chars: '\u24D9\uFF4A\u0135\u01F0\u0249',
    }, {
        base: 'k',
        chars: '\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3',
    }, {
        base: 'l',
        chars: '\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747\u026D',
    }, {
        base: 'lj',
        chars: '\u01C9',
    }, {
        base: 'm',
        chars: '\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F',
    }, {
        base: 'n',
        chars: '\u24DD\uFF4E\u01F9\u0144\xF1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5\u043B\u0509',
    }, {
        base: 'nj',
        chars: '\u01CC',
    }, {
        base: 'o',
        chars: '\u24DE\uFF4F\xF2\xF3\xF4\u1ED3\u1ED1\u1ED7\u1ED5\xF5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\xF6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\xF8\u01FF\uA74B\uA74D\u0275\u0254\u1D11',
    }, {
        base: 'oe',
        chars: '\u0153',
    }, {
        base: 'oi',
        chars: '\u01A3',
    }, {
        base: 'oo',
        chars: '\uA74F',
    }, {
        base: 'ou',
        chars: '\u0223',
    }, {
        base: 'p',
        chars: '\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755\u03C1',
    }, {
        base: 'q',
        chars: '\u24E0\uFF51\u024B\uA757\uA759',
    }, {
        base: 'r',
        chars: '\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783',
    }, {
        base: 's',
        chars: '\u24E2\uFF53\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B\u0282',
    }, {
        base: 'ss',
        chars: '\xDF',
    }, {
        base: 't',
        chars: '\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787',
    }, {
        base: 'th',
        chars: '\u00FE',
    }, {
        base: 'tz',
        chars: '\uA729',
    }, {
        base: 'u',
        chars: '\u24E4\uFF55\xF9\xFA\xFB\u0169\u1E79\u016B\u1E7B\u016D\xFC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289',
    }, {
        base: 'v',
        chars: '\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C',
    }, {
        base: 'vy',
        chars: '\uA761',
    }, {
        base: 'w',
        chars: '\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73',
    }, {
        base: 'x',
        chars: '\u24E7\uFF58\u1E8B\u1E8D',
    }, {
        base: 'y',
        chars: '\u24E8\uFF59\u1EF3\xFD\u0177\u1EF9\u0233\u1E8F\xFF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF',
    }, {
        base: 'z',
        chars: '\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763',
    }
];
/* tslint:enable:max-line-length */
/** @type {?} */
const diacriticsMap = {};
for (let i = 0; i < replacementList.length; i += 1) {
    /** @type {?} */
    const chars = replacementList[i].chars;
    for (let j = 0; j < chars.length; j += 1) {
        diacriticsMap[chars[j]] = replacementList[i].base;
    }
}
/**
 * @param {?} str
 * @return {?}
 */
function removeDiacritics(str) {
    if (!str) {
        return '';
    }
    return str.replace(/[^\u0000-\u007e]/g, (/**
     * @param {?} c
     * @return {?}
     */
    function (c) {
        return diacriticsMap[c] || c;
    }));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class BreadcrumbComponent {
    /**
     * @param {?} wso2IntegrationService
     * @param {?} translate
     * @param {?} route
     * @param {?} router
     */
    constructor(wso2IntegrationService, translate, route, router) {
        this.wso2IntegrationService = wso2IntegrationService;
        this.translate = translate;
        this.route = route;
        this.router = router;
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.subscriptionNavigationEnd = this.router.events
            .pipe(filter((/**
         * @param {?} event
         * @return {?}
         */
        event => event instanceof NavigationEnd)))
            .subscribe((/**
         * @param {?} event
         * @return {?}
         */
        (event) => {
            this.updateItems();
        }));
        this.subscriptionAnimation = this.router.events.subscribe((/**
         * @param {?} event
         * @return {?}
         */
        event => {
            if (event instanceof RouteConfigLoadStart) {
                this.loadingRouteConfig = true;
            }
            else if (event instanceof RouteConfigLoadEnd) {
                setTimeout((/**
                 * @return {?}
                 */
                () => {
                    this.loadingRouteConfig = false;
                }), 500);
            }
        }));
        this.subscriptionModulos = this.wso2IntegrationService.modulos.subscribe({
            next: (/**
             * @param {?} modulos
             * @return {?}
             */
            modulos => {
                this.modulos = modulos.filter((/**
                 * @param {?} modulo
                 * @return {?}
                 */
                modulo => !!modulo.pai));
                this.updateItems();
            })
        });
        this.home = { icon: 'pi pi-home' };
        this.translate.onLangChange.subscribe((/**
         * @param {?} item
         * @return {?}
         */
        item => {
            this.updateItems();
        }));
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.subscriptionNavigationEnd.unsubscribe();
        this.subscriptionAnimation.unsubscribe();
        this.subscriptionModulos.unsubscribe();
    }
    /**
     * @return {?}
     */
    updateItems() {
        if (MapaEnviromentService.get().debug) {
            console.group('Chave para i18n Breadcrumb');
        }
        // const url = this.router.url.split('?')[0];
        /** @type {?} */
        const url = this.getRouteConfig();
        /** @type {?} */
        const modulo = this.modulos.find((/**
         * @param {?} item
         * @return {?}
         */
        item => item.acao === url));
        /** @type {?} */
        let newitems;
        if (modulo) {
            /** @type {?} */
            const navItem = (/** @type {?} */ ({
                id: `${modulo.pai}`,
                label: modulo.nome
            }));
            newitems = this.getItemsForModulo(navItem).reverse();
        }
        else {
            /** @type {?} */
            const breadcrumbs = this.getBreadcrumbs(this.route.snapshot.root).filter((/**
             * @param {?} item
             * @return {?}
             */
            item => !!item));
            newitems = breadcrumbs
                .map((/**
             * @param {?} breadcrumb
             * @return {?}
             */
            (breadcrumb) => {
                return (/** @type {?} */ ({ label: breadcrumb }));
            })).reverse();
        }
        /** @type {?} */
        let parent = '';
        newitems = newitems.map((/**
         * @param {?} item
         * @param {?} i
         * @return {?}
         */
        (item, i) => {
            /** @type {?} */
            let key = `CORE.HEADER.NAVBAR.MENU${parent}.${this.convertKey(item.label)}`;
            if (i + 1 < newitems.length) {
                if (MapaEnviromentService.get().debug) {
                    key = `${key}_`;
                }
            }
            if (MapaEnviromentService.get().debug) {
                console.log(key);
            }
            /** @type {?} */
            const newLabel = this.translate.instant(key);
            if (newLabel !== key) {
                item.label = newLabel;
            }
            parent = parent + '.' + this.convertKey(item.label);
            return item;
        }));
        this.items = newitems;
        if (MapaEnviromentService.get().debug) {
            console.groupEnd();
        }
    }
    /**
     * @private
     * @return {?}
     */
    getRouteConfig() {
        /** @type {?} */
        let routeConfig = '';
        /** @type {?} */
        let currentRoute = this.router.routerState.root;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
            if (!!currentRoute.routeConfig && !!currentRoute.routeConfig.path) {
                routeConfig += ('/' + currentRoute.routeConfig.path);
            }
        }
        return routeConfig;
    }
    /**
     * @param {?} item
     * @return {?}
     */
    getItemsForModulo(item) {
        /** @type {?} */
        let newItem = [item];
        /** @type {?} */
        const parent = this.modulos.find((/**
         * @param {?} modulo
         * @return {?}
         */
        modulo => `${modulo.id}` === item.id));
        if (parent) {
            /** @type {?} */
            const newMenuItem = (/** @type {?} */ ({
                id: `${parent.pai}`,
                label: parent.nome
            }));
            if (parent.tipo === TIPO_MODULO.ITEM_DE_MENU && parent.acao) {
                newMenuItem.routerLink = parent.acao;
            }
            newItem = newItem.concat(this.getItemsForModulo(newMenuItem));
        }
        return newItem;
    }
    /**
     * @param {?} snapshot
     * @return {?}
     */
    getBreadcrumbs(snapshot) {
        /** @type {?} */
        let data = [];
        if (snapshot.firstChild) {
            data = data.concat(this.getBreadcrumbs(snapshot.firstChild));
        }
        if (snapshot.data['breadcrumbs']) {
            data.push(snapshot.data['breadcrumbs']);
        }
        return data;
    }
    /**
     * @private
     * @param {?} text
     * @return {?}
     */
    convertKey(text) {
        return removeDiacritics(text).toUpperCase().replace(/\s/gi, '_');
    }
}
BreadcrumbComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-breadcrumb',
                template: "<div class=\"mapa-breadcrumb-container\">\r\n    <p-breadcrumb [home]=\"home\" [model]=\"items\" [styleClass]=\"'mapa-breadcrumb'\"></p-breadcrumb>\r\n    <p-progressBar [styleClass]=\"loadingRouteConfig ? 'mapa-loading' : 'mapa-loading-hidden'\"\r\n                   mode=\"indeterminate\"></p-progressBar>\r\n</div>\r\n",
                encapsulation: ViewEncapsulation.None,
                styles: [""]
            }] }
];
/** @nocollapse */
BreadcrumbComponent.ctorParameters = () => [
    { type: Wso2IntegrationService },
    { type: TranslateService },
    { type: ActivatedRoute },
    { type: Router }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class HeaderComponent {
    /**
     * @param {?} breakpointObserver
     * @param {?} translateService
     * @param {?} wso2IntegrationService
     * @param {?} route
     * @param {?} router
     */
    constructor(breakpointObserver, translateService, wso2IntegrationService, route, router) {
        this.breakpointObserver = breakpointObserver;
        this.translateService = translateService;
        this.wso2IntegrationService = wso2IntegrationService;
        this.route = route;
        this.router = router;
        this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((/**
         * @param {?} result
         * @return {?}
         */
        result => result.matches)));
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.baseApp = '/' + MapaEnviromentService.get().contexto || `/`;
        this.ambiente = MapaEnviromentService.get().ambiente;
        this.subscriptionAplicacao = this.wso2IntegrationService.aplicacao.subscribe({ next: (/**
             * @param {?} aplicacao
             * @return {?}
             */
            aplicacao => this.aplicacao = aplicacao) });
        this.subscriptionUsuario = this.wso2IntegrationService.usuario.subscribe({ next: (/**
             * @param {?} item
             * @return {?}
             */
            item => this.usuario = item) });
        this.SubscriptionNavigationEnd = this.router.events
            .pipe(filter((/**
         * @param {?} event
         * @return {?}
         */
        event => event instanceof NavigationEnd)))
            .subscribe((/**
         * @param {?} event
         * @return {?}
         */
        (event) => {
            this.updateTitle();
        }));
        this.subscriptionIdioma = this.translateService.onLangChange.subscribe((/**
         * @return {?}
         */
        () => {
            this.updateTitle();
        }));
        this.updateTitle();
    }
    /**
     * @return {?}
     */
    updateTitle() {
        /** @type {?} */
        const snapshot = this.getLastRouter(this.route.snapshot);
        if (snapshot.data && snapshot.data['title']) {
            /** @type {?} */
            const pageTitle = snapshot.data['title'];
            /** @type {?} */
            const key = this.convertKey(pageTitle);
            if (MapaEnviromentService.get().debug) {
                console.log(`Chaves para i18n titulo página: ${key}`);
            }
            this.translateService.get(key).subscribe((/**
             * @param {?} data
             * @return {?}
             */
            (data) => {
                if (data && data !== key) {
                    this.pageTitle = data;
                }
                else {
                    this.pageTitle = pageTitle;
                }
            }));
        }
        else {
            this.pageTitle = null;
        }
    }
    /**
     * @private
     * @param {?} snapshot
     * @return {?}
     */
    getLastRouter(snapshot) {
        if (snapshot.firstChild) {
            return this.getLastRouter(snapshot.firstChild);
        }
        else {
            return snapshot;
        }
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        if (this.subscriptionUsuario) {
            this.subscriptionUsuario.unsubscribe();
        }
        if (this.subscriptionAplicacao) {
            this.subscriptionAplicacao.unsubscribe();
        }
        if (this.SubscriptionNavigationEnd) {
            this.SubscriptionNavigationEnd.unsubscribe();
        }
        if (this.subscriptionIdioma) {
            this.subscriptionIdioma.unsubscribe();
        }
    }
    /**
     * @private
     * @param {?} text
     * @return {?}
     */
    convertKey(text) {
        return removeDiacritics(text).toUpperCase().replace(/\s/gi, '_');
    }
}
HeaderComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-header',
                template: "<div class=\"mapa-header-container\">\r\n    <div [ngClass]=\"{'handset' : (isHandset$ | async)}\">\r\n        <div class=\"mapa-identificacao-ministerio\">\r\n            <a class=\"mapa-brasil-gov\" href=\"http://www.brasil.gov.br\"\r\n               target=\"_blank\">{{ 'CORE.HEADER.MINISTERIO' | translate }}</a>\r\n            <span *ngIf=\"ambiente\" class=\"mapa-ambiente\">{{ambiente}}</span>\r\n        </div>\r\n        <div class=\"mapa-identificacao-sistema-container\">\r\n            <div class=\"mapa-identificacao-sistema\">\r\n                <h1>\r\n                    <a id=\"link-pagina-inicial\" [title]=\"'CORE.HEADER.HOME_TITLE' | translate\" [routerLink]=\"baseApp\">\r\n                        {{aplicacao ? aplicacao.nome : ''}}&nbsp;\r\n\r\n                    </a>\r\n                </h1>\r\n                <h2>\r\n                    {{aplicacao ? aplicacao.versao : ''}} - {{aplicacao ? aplicacao.objetivo : ''}}&nbsp;\r\n                </h2>\r\n            </div>\r\n            <div *ngIf=\"usuario && usuario.nome\" class=\"mapa-dados-login-container\">\r\n                <div class=\"mapa-dados-login\">\r\n                    <ngx-avatar [name]=\"usuario.nome\" initialsSize=\"2\"></ngx-avatar>\r\n                    <span class=\"mapa-dados-login-val\">{{usuario.nome}}</span>\r\n                </div>\r\n                <div class=\"mapa-dados-login-imagem\">\r\n\r\n                </div>\r\n            </div>\r\n        </div>\r\n    </div>\r\n    <mapa-nav-bar></mapa-nav-bar>\r\n    <mapa-breadcrumb></mapa-breadcrumb>\r\n</div>\r\n<div class=\"mapa-page-header\" *ngIf=\"pageTitle!=undefined && pageTitle!=null\">\r\n    <h3>{{pageTitle}}</h3>\r\n</div>\r\n",
                styles: [""]
            }] }
];
/** @nocollapse */
HeaderComponent.ctorParameters = () => [
    { type: BreakpointObserver },
    { type: TranslateService },
    { type: Wso2IntegrationService },
    { type: ActivatedRoute },
    { type: Router }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class LanguageService {
    constructor() {
        MapaEnviromentService.get(this.initLanguage.bind(this));
    }
    /**
     * @private
     * @return {?}
     */
    initLanguage() {
        /** @type {?} */
        let defaultLang = {
            code: 'pt-br',
            name: 'Português',
        };
        /** @type {?} */
        const langCode = this.getLanguage();
        if (langCode && this.getLanguages() && this.getLanguages().length > 0) {
            defaultLang = this.getLanguages().find((/**
             * @param {?} item
             * @return {?}
             */
            item => item.code === langCode)) || defaultLang;
            this.saveLanguage(defaultLang.code);
        }
        this.languageSubject = new BehaviorSubject(defaultLang);
        this.language = this.languageSubject.asObservable();
    }
    /**
     * @return {?}
     */
    getCurrentLanguage() {
        return this.languageSubject.value;
    }
    /**
     * @param {?} lang
     * @return {?}
     */
    updateLanguage(lang) {
        this.saveLanguage(lang ? lang.code : null);
        this.languageSubject.next(lang);
    }
    /**
     * @return {?}
     */
    getLanguages() {
        return MapaEnviromentService.get().idiomas || [];
    }
    /**
     * @private
     * @return {?}
     */
    getLanguage() {
        return localStorage.getItem(`lang-${MapaEnviromentService.get().contexto}`);
    }
    /**
     * @private
     * @param {?} codeLanguage
     * @return {?}
     */
    saveLanguage(codeLanguage) {
        localStorage.setItem(`lang-${MapaEnviromentService.get().contexto}`, codeLanguage);
    }
    /**
     * @private
     * @return {?}
     */
    removeLanguage() {
        localStorage.removeItem(`lang-${MapaEnviromentService.get().contexto}`);
    }
}
LanguageService.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
LanguageService.ctorParameters = () => [];
/** @nocollapse */ LanguageService.ngInjectableDef = defineInjectable({ factory: function LanguageService_Factory() { return new LanguageService(); }, token: LanguageService, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class NavBarComponent {
    /**
     * @param {?} wso2IntegrationService
     * @param {?} appService
     * @param {?} breakpointObserver
     * @param {?} languageService
     * @param {?} translate
     * @param {?} route
     * @param {?} router
     */
    constructor(wso2IntegrationService, appService, breakpointObserver, languageService, translate, route, router) {
        this.wso2IntegrationService = wso2IntegrationService;
        this.appService = appService;
        this.breakpointObserver = breakpointObserver;
        this.languageService = languageService;
        this.translate = translate;
        this.route = route;
        this.router = router;
        this.isHandset$ = this.breakpointObserver.observe(['(max-width: 1024px)']).pipe(map((/**
         * @param {?} result
         * @return {?}
         */
        result => result.matches)));
        this.exibirMenuLateral = false;
        this.nav = [];
        this.navRight = [];
        this.languages = [];
        this.i18nKeyCache = {};
        this.atualizarIdioma = this.atualizarIdioma.bind(this);
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.atualizarMenuLateral();
        this.subscriptionUsuario = this.wso2IntegrationService.usuario.subscribe({
            next: (/**
             * @param {?} user
             * @return {?}
             */
            user => {
                this.usuario = user;
                this.atualizarNavegacaoTopo();
                this.atualizarMenuLateral();
            })
        });
        this.subscriptionAplicacao = this.wso2IntegrationService.aplicacao.subscribe({
            next: (/**
             * @param {?} aplicacao
             * @return {?}
             */
            aplicacao => {
                this.aplicacao = aplicacao;
                this.atualizarNomeAplicacao();
            })
        });
        this.subscriptionModulos = this.wso2IntegrationService.modulos.subscribe({
            next: (/**
             * @param {?} modulos
             * @return {?}
             */
            modulos => {
                // Apenas módulos de menu (DOMINIO e ITEM_DE_MENU)
                this.modulos = (modulos || []).filter((/**
                 * @param {?} modulo
                 * @return {?}
                 */
                modulo => {
                    return [TIPO_MODULO.DOMINIO, TIPO_MODULO.ITEM_DE_MENU].includes(modulo.tipo);
                }));
                this.atualizarNavegacaoTopo();
            })
        });
        this.languages = this.languageService.getLanguages().map((/**
         * @param {?} item
         * @return {?}
         */
        item => ({ label: item.name, value: item.code })));
        this.subscriptionIdioma = this.languageService.language.subscribe((/**
         * @param {?} lang
         * @return {?}
         */
        lang => {
            this.language = lang.code;
            this.translate.use(lang.code).pipe(take(1)).subscribe((/**
             * @return {?}
             */
            () => {
                this.atualizarLabelsTela();
                this.atualizarNomeAplicacao();
                this.atualizarNavegacaoTopo();
            }));
        }));
        this.subscriptionNavigationEnd = this.router.events
            .pipe(filter((/**
         * @param {?} event
         * @return {?}
         */
        event => event instanceof NavigationEnd)))
            .subscribe((/**
         * @param {?} event
         * @return {?}
         */
        (event) => {
            this.exibirMenuLateral = false;
            this.atualizarMenuLateral();
        }));
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        if (this.subscriptionUsuario) {
            this.subscriptionUsuario.unsubscribe();
        }
        if (this.subscriptionAplicacao) {
            this.subscriptionAplicacao.unsubscribe();
        }
        if (this.subscriptionModulos) {
            this.subscriptionModulos.unsubscribe();
        }
        if (this.subscriptionIdioma) {
            this.subscriptionIdioma.unsubscribe();
        }
        if (this.subscriptionI18n) {
            this.subscriptionI18n.unsubscribe();
        }
        if (this.subscriptionNavigationEnd) {
            this.subscriptionNavigationEnd.unsubscribe();
        }
    }
    /**
     * @param {?} $event
     * @return {?}
     */
    fecharMenuLateral($event) {
        this.exibirMenuLateral = false;
    }
    /**
     * @return {?}
     */
    atualizarIdioma() {
        /** @type {?} */
        const selectLang = this.languageService.getLanguages().find((/**
         * @param {?} item
         * @return {?}
         */
        item => item.code === this.language));
        this.languageService.updateLanguage(selectLang);
    }
    /**
     * Atualiza o nome de apresentação da aplicação quando houver alteração de idioma por exemplo
     * @private
     * @return {?}
     */
    atualizarNomeAplicacao() {
        if (this.aplicacao) {
            /** @type {?} */
            const key = this.obterKeyI18n(this.aplicacao.nome);
            this.translate.get(key)
                .subscribe({
                next: (/**
                 * @param {?} item
                 * @return {?}
                 */
                item => {
                    if (item !== this.obterKeyI18n(item.NM_APLICACAO)) {
                        this.appName = item;
                    }
                    else {
                        this.appName = item.NM_APLICACAO;
                    }
                })
            });
        }
    }
    /**
     * @private
     * @return {?}
     */
    atualizarLabelsTela() {
        forkJoin(this.translate.get('CORE.HEADER.NAVBAR.LOGIN'), this.translate.get('CORE.HEADER.NAVBAR.LOGOUT')).subscribe((/**
         * @param {?} resp
         * @return {?}
         */
        resp => {
            this.navRight = this.navRight.map((/**
             * @param {?} item
             * @return {?}
             */
            item => {
                if (item.id === 'login') {
                    item.label = resp[0];
                }
                if (item.id === 'logout') {
                    item.label = resp[1];
                }
                return item;
            }));
        }));
    }
    /**
     * @private
     * @return {?}
     */
    atualizarMenuLateral() {
        this.wso2IntegrationService.isLoggedIn().then((/**
         * @param {?} isLogged
         * @return {?}
         */
        isLogged => {
            /** @type {?} */
            const snapshot = this.getLastRouter(this.route.snapshot);
            this.navRight = [];
            if (snapshot.data && snapshot.data['navRight']) {
                this.navRight = this.navRight.concat(snapshot.data['navRight']);
            }
            // Botão LOGIN
            if (!isLogged) {
                if (snapshot.data && snapshot.data['navRightLogged']) {
                    this.navRight = this.navRight.concat(snapshot.data['navRightLogged']);
                }
                this.translate.get('CORE.HEADER.NAVBAR.LOGIN')
                    .subscribe((/**
                 * @param {?} login
                 * @return {?}
                 */
                login => {
                    this.navRight = this.navRight.filter((/**
                     * @param {?} item
                     * @return {?}
                     */
                    item => item.id !== 'login'));
                    this.navRight.push({
                        id: 'login',
                        label: login,
                        icon: 'pi pi-fw pi-sign-out',
                        command: (/**
                         * @return {?}
                         */
                        () => {
                            this.exibirMenuLateral = false;
                            this.wso2IntegrationService.login();
                        })
                    });
                }));
            }
            else {
                if (snapshot.data && snapshot.data['navRightNotLogged']) {
                    this.navRight = this.navRight.concat(snapshot.data['navRightNotLogged']);
                }
                this.translate.get('CORE.HEADER.NAVBAR.LOGOUT').subscribe((/**
                 * @param {?} logout
                 * @return {?}
                 */
                logout => {
                    this.navRight = this.navRight.filter((/**
                     * @param {?} item
                     * @return {?}
                     */
                    item => item.id !== 'logout'));
                    this.navRight.push({
                        id: 'logout',
                        label: logout,
                        icon: 'pi pi-fw pi-sign-in',
                        command: (/**
                         * @return {?}
                         */
                        () => {
                            this.exibirMenuLateral = false;
                            this.wso2IntegrationService.logout();
                        })
                    });
                }));
            }
        }));
    }
    /**
     * Atualiza os itens de navegação quando os modulos do usuário forem carregados
     * @private
     * @return {?}
     */
    atualizarNavegacaoTopo() {
        if (!this.modulos) {
            return;
        }
        /** @type {?} */
        const moduloPrincipal = this.modulos.find((/**
         * @param {?} modulo
         * @return {?}
         */
        modulo => !modulo.pai));
        if (moduloPrincipal) {
            /** @type {?} */
            const menuNivelUm = this.modulos
                .filter((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => modulo.pai === moduloPrincipal.id))
                .sort((/**
             * @param {?} a
             * @param {?} b
             * @return {?}
             */
            (a, b) => a.ordem - b.ordem))
                .map((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => {
                return {
                    id: `${modulo.id}`,
                    label: modulo.nome,
                    modulo: modulo,
                    automationId: modulo.nome
                };
            }));
            this.nav = this.processarMenuFilho(menuNivelUm);
        }
    }
    /**
     * @private
     * @param {?} menuBase
     * @param {?=} chavePai
     * @return {?}
     */
    processarMenuFilho(menuBase, chavePai = '') {
        return menuBase
            .map((/**
         * @param {?} item
         * @return {?}
         */
        item => {
            /** @type {?} */
            const novaChavePai = chavePai + '.' + this.obterKeyI18n(item.automationId);
            /** @type {?} */
            const filhos = this.modulos
                .filter((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => `${modulo.pai}` === item.id))
                .sort((/**
             * @param {?} a
             * @param {?} b
             * @return {?}
             */
            (a, b) => a.ordem - b.ordem))
                .map((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => {
                /** @type {?} */
                const key = `CORE.HEADER.NAVBAR.MENU${novaChavePai}.${this.obterKeyI18n(modulo.nome)}`;
                /** @type {?} */
                const label = this.translate.instant(key);
                return {
                    id: `${modulo.id}`,
                    label: label !== key ? label : modulo.nome,
                    modulo: modulo,
                    automationId: modulo.nome
                };
            }));
            item.items = this.processarMenuFilho(filhos, novaChavePai) || [];
            // Se o item não tiver filho, obtem o link para ele
            if (item.items.length === 0) {
                item.items = null;
                item.routerLink = item.modulo.acao;
            }
            else {
                /** @type {?} */
                const key = `CORE.HEADER.NAVBAR.MENU${novaChavePai}_`;
                /** @type {?} */
                const label = this.translate.instant(key);
                if (label !== key) {
                    item.label = label;
                }
            }
            return item;
        }))
            .filter((/**
         * @param {?} item
         * @return {?}
         */
        item => {
            return !!item.routerLink || (!item.routerLink && item.items && item.items.length > 0);
        }));
    }
    /**
     * Obtém uma chave i18n válido a partir da string informada
     * @private
     * @param {?} text
     * @return {?}
     */
    obterKeyI18n(text) {
        if (!this.i18nKeyCache.hasOwnProperty(text)) {
            this.i18nKeyCache[text] = removeDiacritics(text)
                .toUpperCase()
                .replace(/\s/gi, '_');
        }
        return this.i18nKeyCache[text];
    }
    /**
     * @private
     * @param {?} snapshot
     * @return {?}
     */
    getLastRouter(snapshot) {
        if (snapshot.firstChild) {
            return this.getLastRouter(snapshot.firstChild);
        }
        else {
            return snapshot;
        }
    }
}
NavBarComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-nav-bar',
                template: "<div class=\"mapa-navbar-container\">\r\n    <div *ngIf=\"!(isHandset$ | async)\" class=\"mapa-navbar-desktop\">\r\n        <p-menubar [model]=\"nav\" [styleClass]=\"'mapa-menubar'\"></p-menubar>\r\n        <mapa-search-nav-item (changeRouter)=\"fecharMenuLateral($event)\"></mapa-search-nav-item>\r\n        <p-dropdown (onChange)=\"atualizarIdioma()\" *ngIf=\"languages.length > 0\" [(ngModel)]=\"language\"\r\n                    [options]=\"languages\"\r\n                    [styleClass]=\"'mapa-lang'\" placeholder=\"Language\"></p-dropdown>\r\n        <p-menubar [autoDisplay]=\"false\" [model]=\"navRight\" [styleClass]=\"'mapa-menubar'\"></p-menubar>\r\n    </div>\r\n    <div *ngIf=\"isHandset$ | async\" class=\"mapa-navbar-mobile\">\r\n        <p-sidebar [(visible)]=\"exibirMenuLateral\" [showCloseIcon]=\"false\" [styleClass]=\"'mapa-menubar-mobile-sidebar'\">\r\n            <div class=\"mapa-menubar-mobile-head\">\r\n                <div>\r\n                    <ngx-avatar [name]=\"usuario ? usuario.nome : 'MAPA'\" initialsSize=\"2\" size=\"80\"></ngx-avatar>\r\n                </div>\r\n                <div>\r\n               <span\r\n                   class=\"mapa-dados-login-val\">{{usuario ? usuario.nome : 'CORE.HEADER.NOT_AUTHENTICATED' | translate}}</span>\r\n                </div>\r\n            </div>\r\n            <div class=\"mapa-menubar-mobile-content\">\r\n                <div class=\"mapa-menubar-mobile-search-nav-item\">\r\n                    <mapa-search-nav-item (changeRouter)=\"fecharMenuLateral($event)\"></mapa-search-nav-item>\r\n                </div>\r\n                <div class=\"mapa-menubar-mobile-lang\">\r\n                    <p-dropdown (onChange)=\"atualizarIdioma()\" *ngIf=\"languages.length > 0\" [(ngModel)]=\"language\"\r\n                                [options]=\"languages\"\r\n                                placeholder=\"Language\"></p-dropdown>\r\n                </div>\r\n                <p-panelMenu [model]=\"nav\" [styleClass]=\"'mapa-menubar-mobile-nav'\"></p-panelMenu>\r\n                <p-panelMenu [model]=\"navRight\" [styleClass]=\"'mapa-menubar-mobile-nav'\"></p-panelMenu>\r\n            </div>\r\n        </p-sidebar>\r\n        <button (click)=\"exibirMenuLateral = true\" icon=\"pi pi-bars\" pButton type=\"text\"></button>\r\n        <h1>{{appName}}</h1>\r\n    </div>\r\n\r\n</div>\r\n",
                encapsulation: ViewEncapsulation.None,
                styles: [""]
            }] }
];
/** @nocollapse */
NavBarComponent.ctorParameters = () => [
    { type: Wso2IntegrationService },
    { type: AplicacaoAPI },
    { type: BreakpointObserver },
    { type: LanguageService },
    { type: TranslateService },
    { type: ActivatedRoute },
    { type: Router }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class SearchNavItemComponent {
    /**
     * @param {?} wso2IntegrationService
     * @param {?} route
     */
    constructor(wso2IntegrationService, route) {
        this.wso2IntegrationService = wso2IntegrationService;
        this.route = route;
        this.resultsMenu = [];
        this.changeRouter = new EventEmitter();
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.updateModulos();
    }
    /**
     * @return {?}
     */
    updateModulos() {
        this.subscriptionModulos = this.wso2IntegrationService.modulos.subscribe({
            next: (/**
             * @param {?} modulos
             * @return {?}
             */
            modulos => {
                this.modulos = modulos.filter((/**
                 * @param {?} modulo
                 * @return {?}
                 */
                modulo => {
                    return modulo.pai !== null && modulo.pai !== undefined && modulo.pai !== 0;
                }));
            })
        });
    }
    /**
     * Busca menu
     * @param {?} event
     * @return {?}
     */
    search(event) {
        /** @type {?} */
        const query = event.query;
        /** @type {?} */
        const regex = new RegExp(query, 'gi');
        this.resultsMenu = this.modulos
            .filter((/**
         * @param {?} modulo
         * @return {?}
         */
        modulo => modulo.tipo === TIPO_MODULO.ITEM_DE_MENU && modulo.acao))
            .map((/**
         * @param {?} modulo
         * @return {?}
         */
        modulo => ({
            label: modulo.nome,
            id: `${modulo.id}`,
            routerLink: modulo.acao
        })))
            .map((/**
         * @param {?} item
         * @return {?}
         */
        item => {
            /** @type {?} */
            const parents = this.getItemsForModulo(item);
            parents.shift();
            item.label = parents.map((/**
             * @param {?} parent
             * @return {?}
             */
            parent => parent.label))
                .reverse()
                .join(' > ');
            return item;
        }))
            .filter((/**
         * @param {?} item
         * @return {?}
         */
        item => regex.test(item.label)));
    }
    /**
     * @param {?} menuItem
     * @return {?}
     */
    onSelect(menuItem) {
        this.searchMenu = null;
        if (menuItem && menuItem.routerLink) {
            this.changeRouter.emit(menuItem);
            this.route.navigate([menuItem.routerLink]);
        }
    }
    /**
     * @param {?} item
     * @return {?}
     */
    getItemsForModulo(item) {
        /** @type {?} */
        let newItem = [item];
        /** @type {?} */
        const parent = this.modulos.find((/**
         * @param {?} modulo
         * @return {?}
         */
        modulo => `${modulo.id}` === item.id));
        if (parent) {
            /** @type {?} */
            const newMenuItem = (/** @type {?} */ ({
                id: `${parent.pai}`,
                label: parent.nome
            }));
            if (parent.tipo === TIPO_MODULO.ITEM_DE_MENU && parent.acao) {
                newMenuItem.routerLink = parent.acao;
            }
            newItem = newItem.concat(this.getItemsForModulo(newMenuItem));
        }
        return newItem;
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        if (this.subscriptionModulos) {
            this.subscriptionModulos.unsubscribe();
        }
    }
}
SearchNavItemComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-search-nav-item',
                template: "<form class=\"search-nav-item\">\r\n    <p-autoComplete (completeMethod)=\"search($event)\" (onSelect)=\"onSelect($event)\" [(ngModel)]=\"searchMenu\"\r\n                    [suggestions]=\"resultsMenu\" field=\"label\" name=\"search\"\r\n                    [placeholder]=\"'CORE.HEADER.NAVBAR.SEARCH_NAV' | translate\"></p-autoComplete>\r\n</form>\r\n"
            }] }
];
/** @nocollapse */
SearchNavItemComponent.ctorParameters = () => [
    { type: Wso2IntegrationService },
    { type: Router }
];
SearchNavItemComponent.propDecorators = {
    changeRouter: [{ type: Output }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class HideForGroupsDirective {
    /**
     * @param {?} templateRef
     * @param {?} vcRef
     * @param {?} wso2IntegrationService
     */
    constructor(templateRef, vcRef, wso2IntegrationService) {
        this.templateRef = templateRef;
        this.vcRef = vcRef;
        this.wso2IntegrationService = wso2IntegrationService;
    }
    /**
     * @param {?} profiles
     * @return {?}
     */
    set hideForGroups(profiles) {
        this.wso2IntegrationService.usuario.subscribe({
            next: (/**
             * @param {?} usuario
             * @return {?}
             */
            usuario => {
                if (usuario && usuario.idUsuario) {
                    if (!Array.isArray(profiles)) {
                        profiles = [profiles];
                    }
                    if (usuario.hasAnyPerfil(profiles)) {
                        this.vcRef.clear();
                    }
                    else {
                        this.vcRef.createEmbeddedView(this.templateRef);
                    }
                }
                else {
                    console.log('Não foi identificado perfil/grupo para o usuário atual, por isso o componente será renderizado...');
                    this.vcRef.createEmbeddedView(this.templateRef);
                }
            })
        });
    }
}
HideForGroupsDirective.decorators = [
    { type: Directive, args: [{
                selector: '[hideForGroups]'
            },] }
];
/** @nocollapse */
HideForGroupsDirective.ctorParameters = () => [
    { type: TemplateRef },
    { type: ViewContainerRef },
    { type: Wso2IntegrationService }
];
HideForGroupsDirective.propDecorators = {
    hideForGroups: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class ShowForGroupsDirective {
    /**
     * @param {?} templateRef
     * @param {?} vcRef
     * @param {?} wso2IntegrationService
     */
    constructor(templateRef, vcRef, wso2IntegrationService) {
        this.templateRef = templateRef;
        this.vcRef = vcRef;
        this.wso2IntegrationService = wso2IntegrationService;
    }
    /**
     * @param {?} profiles
     * @return {?}
     */
    set showForGroups(profiles) {
        this.wso2IntegrationService.usuario.subscribe({
            next: (/**
             * @param {?} usuario
             * @return {?}
             */
            usuario => {
                if (usuario && usuario.idUsuario) {
                    if (!Array.isArray(profiles)) {
                        profiles = [profiles];
                    }
                    if (usuario.hasAnyPerfil(profiles)) {
                        this.vcRef.createEmbeddedView(this.templateRef);
                    }
                    else {
                        this.vcRef.clear();
                    }
                }
                else {
                    console.log('Não foi identificado perfil/grupo para o usuário atual, por isso o componente será renderizado...');
                    this.vcRef.createEmbeddedView(this.templateRef);
                }
            })
        });
    }
}
ShowForGroupsDirective.decorators = [
    { type: Directive, args: [{
                selector: '[showForGroups]'
            },] }
];
/** @nocollapse */
ShowForGroupsDirective.ctorParameters = () => [
    { type: TemplateRef },
    { type: ViewContainerRef },
    { type: Wso2IntegrationService }
];
ShowForGroupsDirective.propDecorators = {
    showForGroups: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class AdminLayoutComponent {
    constructor() {
        this.show$ = new BehaviorSubject(false);
        // Só exibe após a configuração ser definida pela aplicação
        MapaEnviromentService.get((/**
         * @return {?}
         */
        () => {
            this.show$.next(true);
        }));
    }
}
AdminLayoutComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-admin-layout',
                template: "<div *ngIf=\"(show$ | async)\">\r\n    <mapa-header></mapa-header>\r\n    <div class=\"mapa-content-container\">\r\n        <router-outlet></router-outlet>\r\n    </div>\r\n</div>\r\n",
                styles: [""]
            }] }
];
/** @nocollapse */
AdminLayoutComponent.ctorParameters = () => [];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class BlankLayoutComponent {
    constructor() {
        this.show$ = new BehaviorSubject(false);
        // Só exibe após a configuração ser definida pela aplicação
        MapaEnviromentService.get((/**
         * @return {?}
         */
        () => {
            this.show$.next(true);
        }));
    }
}
BlankLayoutComponent.decorators = [
    { type: Component, args: [{
                selector: 'mapa-blank-layout',
                template: "<div *ngIf=\"(show$ | async)\">\r\n    <router-outlet></router-outlet>\r\n</div>\r\n",
                styles: [""]
            }] }
];
/** @nocollapse */
BlankLayoutComponent.ctorParameters = () => [];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const UiRoutes = [];
class UiModule {
}
UiModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    TranslateModule,
                    FormsModule,
                    AutoCompleteModule,
                    AvatarModule,
                    CommonModule,
                    RouterModule.forChild(UiRoutes),
                    SidebarModule,
                    SlideMenuModule,
                    DropdownModule,
                    MenubarModule,
                    ButtonModule,
                    InputTextModule,
                    BreadcrumbModule,
                    PanelMenuModule,
                    ProgressBarModule
                ],
                declarations: [
                    BlankLayoutComponent,
                    AdminLayoutComponent,
                    PublicLayoutComponent,
                    NavBarComponent,
                    SearchNavItemComponent,
                    BreadcrumbComponent,
                    HeaderComponent,
                    ShowForGroupsDirective,
                    HideForGroupsDirective
                ],
                exports: [
                    BlankLayoutComponent,
                    AdminLayoutComponent,
                    PublicLayoutComponent,
                    ShowForGroupsDirective,
                    HideForGroupsDirective
                ],
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class SharedModule {
}
SharedModule.decorators = [
    { type: NgModule, args: [{
                imports: [UiModule],
                exports: [UiModule, TranslateModule]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Se o usuário selecionar um idioma, envia em todas as requisições essa solicitação
 */
class LanguageInterceptor {
    /**
     * @param {?} languageService
     */
    constructor(languageService) {
        this.languageService = languageService;
    }
    /**
     * @param {?} request
     * @param {?} next
     * @return {?}
     */
    intercept(request, next) {
        /** @type {?} */
        const lang = this.languageService.getCurrentLanguage();
        if (lang && lang.code) {
            request = request.clone({
                setHeaders: {
                    'Accept-Language': `${lang.code}`
                }
            });
        }
        return next.handle(request);
    }
}
LanguageInterceptor.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LanguageInterceptor.ctorParameters = () => [
    { type: LanguageService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const regexIgnore = /\.(ico|js|json|png|jpg|jpeg|css|ttf|woff2)$/;
/**
 * Em toda requisição informa o token atual
 */
class JwtInterceptor {
    /**
     * @param {?} http
     * @param {?} wso2IntegrationService
     */
    constructor(http, wso2IntegrationService) {
        this.http = http;
        this.wso2IntegrationService = wso2IntegrationService;
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject = new BehaviorSubject(null);
    }
    /**
     * @param {?} request
     * @param {?} next
     * @return {?}
     */
    intercept(request, next) {
        /** @type {?} */
        const skipAuth = 'true' === request.headers.get('skipAuth');
        // Ignora o uso do token em algumas requisições específicas
        if (skipAuth || request.url.indexOf('/MapaAuthorization/1.0.0/') > 0 || regexIgnore.test(request.url) || request.url.endsWith('/token')) {
            return next.handle(request);
        }
        request = this.addAuthenticationToken(request);
        return next.handle(request).pipe(catchError((/**
         * @param {?} error
         * @return {?}
         */
        (error) => {
            console.error('JwtInterceptor :: Erro na requisição: ', error);
            // {"fault": {
            //     "code": 900901,
            //     "message": "Invalid Credentials",
            //     "description": "Access failure for API: /comex/1.0.0, version: 1.0.0 status: (900901) - Invalid Credentials. Make sure you have given the correct access token"
            // }}
            /** @type {?} */
            const body = error.error;
            if ([0, 401].includes(error.status) || (body && body.fault && body.fault.code === 900901)) {
                console.log('Erro: 401 - Unauthorized | Um novo "access token" será gerado e a requisição será executada mais uma vez');
                if (this.refreshTokenInProgress) {
                    return this.refreshTokenSubject.pipe(filter((/**
                     * @param {?} result
                     * @return {?}
                     */
                    result => result !== null)), take(1), switchMap((/**
                     * @return {?}
                     */
                    () => next.handle(this.addAuthenticationToken(request)))));
                }
                else {
                    this.refreshTokenInProgress = true;
                    this.refreshTokenSubject.next(null);
                    return this.refreshAccessToken().pipe(switchMap((/**
                     * @param {?} success
                     * @return {?}
                     */
                    (success) => {
                        this.refreshTokenSubject.next(success);
                        return next.handle(this.addAuthenticationToken(request));
                    })), finalize((/**
                     * @return {?}
                     */
                    () => this.refreshTokenInProgress = false)));
                }
            }
            else {
                return throwError(error);
            }
        })));
    }
    /**
     * @private
     * @param {?} request
     * @return {?}
     */
    addAuthenticationToken(request) {
        /** @type {?} */
        const config = MapaEnviromentService.get();
        /** @type {?} */
        const tokenJWT = this.wso2IntegrationService.getTokenJWT();
        /** @type {?} */
        const jwt = this.wso2IntegrationService.getJWT();
        /** @type {?} */
        const headers = {};
        /** @type {?} */
        let newHeaders = false;
        // No ambiente local adiciona o cabeçalho que é criado pelo WSO2 com o token jwt
        if (config.localUrl && config.ambiente.toLowerCase() === 'local' && request.url.startsWith(config.localUrl)) {
            newHeaders = true;
            headers['MAPA-JWT-ASSERTION'] = `Bearer ${tokenJWT}`;
        }
        else if (jwt && jwt.ACCESS_TOKEN) {
            newHeaders = true;
            headers['Authorization'] = `Bearer ${jwt.ACCESS_TOKEN}`;
        }
        if (newHeaders) {
            return request.clone({
                setHeaders: headers
            });
        }
        return request;
    }
    /**
     * @private
     * @return {?}
     */
    refreshAccessToken() {
        /** @type {?} */
        const config = MapaEnviromentService.get();
        /** @type {?} */
        const jwt = this.wso2IntegrationService.getJWT();
        /** @type {?} */
        const urlGetToken = config.wso2Url.endsWith('/') ? config.wso2Url + 'token' : config.wso2Url + '/token';
        return fromFetch(urlGetToken, {
            method: 'POST',
            body: 'grant_type=client_credentials',
            headers: {
                'Accept': '*/*',
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': 'Basic ' + jwt.BASIC
            }
        }).pipe(switchMap((/**
         * @param {?} response
         * @return {?}
         */
        response => {
            if (response.ok) {
                return from(response.json());
            }
            else {
                return throwError({ status: `${response.status} - ${response.statusText}`, message: `${response.text()}` });
            }
        })), map((/**
         * @param {?} data
         * @return {?}
         */
        (data) => this.wso2IntegrationService.changeAccessToken(data.access_token))), map((/**
         * @return {?}
         */
        () => true)), catchError((/**
         * @param {?} err
         * @return {?}
         */
        err => {
            console.error(err);
            return throwError({ message: err.message });
        })));
    }
}
JwtInterceptor.decorators = [
    { type: Injectable }
];
/** @nocollapse */
JwtInterceptor.ctorParameters = () => [
    { type: HttpClient },
    { type: Wso2IntegrationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
registerLocaleData(localePt, 'pt', localePtExtra);
// AoT requires an exported function for factories
/**
 * @param {?} http
 * @return {?}
 */
function HttpLoaderFactory(http) {
    return new TranslateHttpLoader(http, 'assets/i18n/');
}
class MapaCoreInternalModule {
}
MapaCoreInternalModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    HttpClientModule,
                    AuthModule,
                    SharedModule,
                    TranslateModule.forRoot({
                        loader: {
                            provide: TranslateLoader,
                            useFactory: HttpLoaderFactory,
                            deps: [HttpClient]
                        }
                    }),
                    OAuthModule.forRoot()
                ],
                exports: [
                    CommonModule,
                    FormsModule,
                    ReactiveFormsModule,
                    HttpClientModule,
                    AuthModule,
                    SharedModule
                ]
            },] }
];
class MapaCoreModule {
    /**
     * @param {?} translate
     * @param {?} wso2IntegrationService
     */
    constructor(translate, wso2IntegrationService) {
        // this language will be used as a fallback when a translation isn't found in the current language
        translate.setDefaultLang('pt-br');
        // the lang to use, if the lang isn't available, it will use the current loader to get them
        translate.use('pt-br');
    }
    /**
     * @return {?}
     */
    static forRoot() {
        return {
            ngModule: MapaCoreInternalModule,
            providers: [
                { provide: LOCALE_ID, useValue: 'pt' },
                { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
                { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true }
            ]
        };
    }
    /**
     * Inicio da aplicação.
     *
     * Deve ser invocado no main.ts da aplicação final.
     *
     * Faz o carregamento do enviroment.json a partir do ambiente de execução (Docker)
     * @template M
     * @param {?} defaultEnvironment
     * @return {?}
     */
    static loadEnvironment(defaultEnvironment) {
        /** @type {?} */
        const promise = new Promise((/**
         * @param {?} resolve
         * @param {?} reject
         * @return {?}
         */
        (resolve, reject) => {
            /** @type {?} */
            const xmlhttp = new XMLHttpRequest();
            /** @type {?} */
            const method = 'GET';
            /** @type {?} */
            const url = 'assets/environment.json';
            xmlhttp.open(method, url, true);
            xmlhttp.onload = (/**
             * @return {?}
             */
            () => {
                if (xmlhttp.status === 200) {
                    resolve(JSON.parse(xmlhttp.responseText));
                }
                else {
                    resolve(defaultEnvironment);
                }
            });
            xmlhttp.send();
        }));
        return promise.then((/**
         * @param {?} envJson
         * @return {?}
         */
        envJson => {
            if (envJson.production) {
                enableProdMode();
            }
            // Faz a copia de todos os parametros de ambiente para a configuração atual
            if (envJson.mapa) {
                // tslint:disable-next-line
                for (const a in envJson.mapa) {
                    if (!envJson.mapa.hasOwnProperty(a)) {
                        continue;
                    }
                    defaultEnvironment.mapa[a] = envJson.mapa[a];
                }
            }
            if (!defaultEnvironment.mapa.sso) {
                throw new Error('A configuração "sso" não foi definido no enviroment.json');
            }
            MapaEnviromentService.set(defaultEnvironment.mapa);
            if (defaultEnvironment.mapa.debug) {
                console.log('@mapa/core: environment', defaultEnvironment);
            }
        }));
    }
}
MapaCoreModule.decorators = [
    { type: NgModule, args: [{
                imports: [CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule, SharedModule],
                exports: [CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule, SharedModule],
                providers: [
                    { provide: LOCALE_ID, useValue: 'pt' }
                ]
            },] }
];
/** @nocollapse */
MapaCoreModule.ctorParameters = () => [
    { type: TranslateService },
    { type: Wso2IntegrationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class AuthGuard {
    /**
     * @param {?} router
     * @param {?} wso2IntegrationService
     */
    constructor(router, wso2IntegrationService) {
        this.router = router;
        this.wso2IntegrationService = wso2IntegrationService;
        this.subscriptionModulos = this.wso2IntegrationService.modulos.subscribe({
            next: (/**
             * @param {?} modulos
             * @return {?}
             */
            modulos => this.modulos = modulos || [])
        });
    }
    /**
     * @private
     * @param {?} route
     * @return {?}
     */
    static getRouteConfig(route) {
        /** @type {?} */
        let routeConfig = '';
        /** @type {?} */
        let currentRoute = route;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
            if (!!currentRoute.routeConfig && !!currentRoute.routeConfig.path) {
                routeConfig += ('/' + currentRoute.routeConfig.path);
            }
        }
        return routeConfig;
    }
    /**
     * @param {?} childRoute
     * @param {?} state
     * @return {?}
     */
    canActivateChild(childRoute, state) {
        return this.canActivate(childRoute, state);
    }
    /**
     * @param {?} route
     * @param {?} state
     * @return {?}
     */
    canActivate(route, state) {
        return this.wso2IntegrationService.usuario.pipe(filter((/**
         * @param {?} value
         * @return {?}
         */
        value => !!value)), take(1), map((/**
         * @return {?}
         */
        () => {
            /** @type {?} */
            const url = state.url;
            /** @type {?} */
            const routeConfig = AuthGuard.getRouteConfig(state.root);
            /** @type {?} */
            const result = ['', '/'].includes(routeConfig) || !!this.modulos.find((/**
             * @param {?} modulo
             * @return {?}
             */
            modulo => modulo.acao === routeConfig));
            if (!result) {
                console.error(`AuthGuard::canActivate() -> SEM PERMISSÃO DE ACESSO | routeConfig: "${routeConfig}" | url: "${url}"`);
            }
            return result;
        })));
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        if (this.subscriptionModulos) {
            this.subscriptionModulos.unsubscribe();
        }
    }
}
AuthGuard.decorators = [
    { type: Injectable, args: [{ providedIn: 'root' },] }
];
/** @nocollapse */
AuthGuard.ctorParameters = () => [
    { type: Router },
    { type: Wso2IntegrationService }
];
/** @nocollapse */ AuthGuard.ngInjectableDef = defineInjectable({ factory: function AuthGuard_Factory() { return new AuthGuard(inject(Router), inject(Wso2IntegrationService)); }, token: AuthGuard, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class AuthenticationService {
    /**
     * @param {?} wso2IntegrationService
     */
    constructor(wso2IntegrationService) {
        this.wso2IntegrationService = wso2IntegrationService;
        this.wso2IntegrationService.usuario.subscribe({
            next: (/**
             * @param {?} usuario
             * @return {?}
             */
            usuario => {
                this.usuario = usuario;
            })
        });
    }
    /**
     * @return {?}
     */
    getUsuarioLogado() {
        return this.usuario;
    }
    /**
     * @param {?} perfil
     * @return {?}
     */
    hasPerfil(perfil) {
        return !!this.usuario && this.usuario.hasPerfil(perfil);
    }
    /**
     * @param {?} perfis
     * @return {?}
     */
    hasAnyPerfil(perfis) {
        return !!this.usuario && this.usuario.hasAnyPerfil(perfis);
    }
}
AuthenticationService.decorators = [
    { type: Injectable }
];
/** @nocollapse */
AuthenticationService.ctorParameters = () => [
    { type: Wso2IntegrationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { AdminLayoutComponent, AuthGuard, AuthenticationService, BlankLayoutComponent, HttpLoaderFactory, LanguageService, MapaCoreInternalModule, MapaCoreModule, MapaEnviromentService, PublicLayoutComponent, SharedModule, Usuario, WSO2, WSO2Client, Wso2IntegrationService, AuthModule as ɵa, authRoutes as ɵb, AuthRouting as ɵc, AuthComponent as ɵd, AplicacaoAPI as ɵe, GruposAPI as ɵf, ModulosAPI as ɵg, UiRoutes as ɵh, UiModule as ɵi, NavBarComponent as ɵj, SearchNavItemComponent as ɵk, BreadcrumbComponent as ɵl, HeaderComponent as ɵm, ShowForGroupsDirective as ɵn, HideForGroupsDirective as ɵo, JwtInterceptor as ɵp, LanguageInterceptor as ɵq };
//# sourceMappingURL=mapa-core.js.map
